import React, { useState, useEffect, useRef } from 'react';
import { X, AlertTriangle, FileUp } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import AttachmentPreviewModal from '../Common/AttachmentPreviewModal';
import {
  DEFAULT_ALLOWED_UPLOAD_ACCEPT,
  validateUploadFile,
} from '../Common/uploadValidation';
import {
  uploadFileToR2,
  createInvestmentAttachment,
  reviewInvestmentAttachment,
  deleteInvestmentAttachment,
  getInvestmentAttachmentUrl,
  getInvestmentAttachmentDownloadUrl,
} from '../../services/proofApi';
import { payrollService, employeeService } from '../../services/api';
import { getNumberRangeError, getPanError, getAddressError, employeeRequiresPrevEmploymentDetails, validatePrevEmploymentFields, normalizePrevEmploymentForSave } from '../../utils/investmentValidation';

// --------------------- FloatingInput Component ---------------------
const FloatingInput = ({
  id,
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  disabled = false,
  error = null,
}) => {
  const handleChange = (e) => {
    const newValue = e.target.value;
    // For required fields, prevent empty strings
    if (required && newValue === '') {
      // Don't update if it's an empty string for required fields
      // But still call onChange so parent can handle validation
      if (onChange) {
        onChange(e);
      }
      return;
    }
    if (onChange) {
      onChange(e);
    }
  };

  const handleBlur = (e) => {
    // For required fields, ensure they don't remain empty
    if (required && (!e.target.value || e.target.value.trim() === '')) {
      e.target.value = '';
      // Trigger validation by calling onChange
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: '',
          }
        };
        onChange(syntheticEvent);
      }
    }
  };

  return (
    <div className="relative mb-4">
      <input
        id={id}
        type={type}
        value={value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder=" "
        required={required}
        disabled={disabled}
        className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-white rounded-none border-0 ${
          error ? 'border-b-2 border-red-500' : 'border-b border-gray-300 focus:border-b-2 focus:border-redhalo-red'
        } appearance-none focus:outline-none focus:ring-0 hover:border-b-redhalo-navy peer disabled:bg-gray-100 disabled:text-gray-500`}
      />
      <label
        htmlFor={id}
        className={`absolute text-sm ${
          error ? 'text-red-500' : 'text-gray-500'
        } duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 rounded-none 
          peer-focus:px-2 peer-focus:text-redhalo-navy 
          peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 
          peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1`}
      >
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
};

// --------------------- SectionUpload Component ---------------------
const SectionUpload = ({
  sectionName,
  onUpload,
  onApprove,
  onReject,
  onRemove,
  disabled = false,
  isSectionLevel = false,
  initialStatus = 'pending',
  initialRejectReason = '',
  initialAttachmentId = null,
  initialFileName,
  initialFileSize,
  onViewAttachment,
  infoText,
}) => {
  const [file, setFile] = useState(
    initialFileName ? { name: initialFileName, size: initialFileSize || 0, type: '' } : null
  );
  const [status, setStatus] = useState(initialStatus); // pending, approved, rejected
  const [rejectReason, setRejectReason] = useState(initialRejectReason);
  const [photoModal, setPhotoModal] = useState({ isOpen: false, photo: null, fileName: '' });
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReasonDraft, setRejectReasonDraft] = useState('');
  const [attachmentId, setAttachmentId] = useState(initialAttachmentId);
  const [uploadError, setUploadError] = useState('');
  const [showInfo, setShowInfo] = useState(false);

  // Keep internal state in sync when initial props change (after attachments load or selection changes)
  useEffect(() => {
    if (initialFileName) {
      setFile({ name: initialFileName, size: initialFileSize || 0, type: '' });
      if (initialAttachmentId) {
        setAttachmentId(initialAttachmentId);
      }
    }
  }, [initialFileName, initialFileSize, initialAttachmentId]);

  // Keep status and reject reason in sync with latest backend values
  useEffect(() => {
    setStatus(initialStatus || 'pending');
    setRejectReason(initialRejectReason || '');
  }, [initialStatus, initialRejectReason]);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // If the same file name is selected again for this field, block and show a clear message
    if (file && selectedFile.name === file.name) {
      alert(`You already uploaded '${selectedFile.name}' for '${sectionName}'. Please choose a different file or rename it.`);
      return;
    }

    const validation = validateUploadFile(selectedFile);
    if (!validation.ok) {
      alert(`${validation.error} for '${sectionName}'.`);
      return;
    }

    setUploadError('');

    setFile(selectedFile);
    setStatus('pending');

    // If it's an image, show preview
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoModal({
          isOpen: true,
          photo: reader.result,
          fileName: selectedFile.name,
        });
      };
      reader.readAsDataURL(selectedFile);
    }

    if (typeof onUpload === 'function') {
      try {
        const result = await onUpload({
          file: selectedFile,
          sectionName,
          isSectionLevel,
        });
        if (result && result.attachmentId) {
          setAttachmentId(result.attachmentId);
        }
      } catch (err) {
        console.error('Failed to upload proof:', err);
        setUploadError('Failed to upload proof. Please try again.');
        alert(`Failed to upload proof for '${sectionName}' (${selectedFile.name}). Please try again.`);
        setFile(null);
        setAttachmentId(null);
      }
    }
  };

  const handleViewPhoto = () => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoModal({ 
          isOpen: true, 
          photo: reader.result, 
          fileName: file.name 
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const closePhotoModal = () => {
    setPhotoModal({ isOpen: false, photo: null, fileName: '' });
  };

  const downloadPhoto = () => {
    if (photoModal.photo) {
      const link = document.createElement('a');
      link.href = photoModal.photo;
      link.download = photoModal.fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleApprove = async () => {
    if (disabled) return;

    // Primary path: for existing attachments, always go through onApprove
    // even if local file state is null (e.g., after reload).
    if (attachmentId && typeof onApprove === 'function') {
      try {
        await onApprove({ attachmentId, sectionName });
        setStatus('approved');
      } catch (err) {
        console.error('Failed to approve attachment:', err);
        alert('Failed to approve document. Please try again.');
      }
      return;
    }

    // Backwards-compatibility: fall back to old onUpload contract if no onApprove provided
    if (typeof onUpload === 'function' && file) {
      onUpload({
        sectionName,
        fileName: file.name,
        size: file.size,
        status: 'approved',
        lastUpdated: new Date().toISOString(),
        isSectionLevel,
      });
      setStatus('approved');
    }
  };

  const handleReject = () => {
    if (disabled || !attachmentId) return;
    setRejectReasonDraft(rejectReason || '');
    setIsRejectModalOpen(true);
  };

  // Prefer the real uploaded filename when we have it; otherwise fall back to
  // the logical section label so the UI never shows an empty name.
  const displayName = file?.name || initialFileName || sectionName;
  const displaySizeBytes =
    file?.size != null ? file.size : initialFileSize != null ? initialFileSize : 0;

  return (
    <div className="mt-2 p-2 border border-gray-200 bg-gray-50 relative">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center space-x-2">
          {/* Photo Preview/Display for newly selected images (not shown once rejected).
              When an attachmentId already exists, rely on the backend view icon instead
              of showing a second local preview eye icon. */}
          {file && !attachmentId && status !== 'rejected' && file.type.startsWith('image/') && (
            <div className="mr-2">
              <div
                className="w-8 h-8 bg-redhalo-mint/60 flex items-center justify-center cursor-pointer"
                onClick={handleViewPhoto}
              >
                <img
                  src={photoModal.photo || ''}
                  alt="Document Preview"
                  className="w-6 h-6 object-cover rounded"
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-1">
            {/* Local preview for newly selected images (before upload or for quick check).
                When attachmentId exists, use the backend-driven view icon instead of this one. */}
            {file && !attachmentId && status !== 'rejected' && file.type.startsWith('image/') && (
              <button
                type="button"
                onClick={handleViewPhoto}
                className="w-6 h-6 text-redhalo-navy bg-redhalo-mint/40 border border-redhalo-teal/50 rounded hover:bg-redhalo-mint/60 flex items-center justify-center"
                title="View Document"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            )}

            {/* Upload / replace document (always first action icon) */}
            <div className="relative">
              <input
                type="file"
                accept={DEFAULT_ALLOWED_UPLOAD_ACCEPT}
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                id={`file-upload-${sectionName}`}
                disabled={disabled}
              />
              <label
                htmlFor={`file-upload-${sectionName}`}
                className={`w-6 h-6 text-purple-600 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 flex items-center justify-center cursor-pointer ${
                  disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                title={file ? "Replace Document" : "Upload Document"}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </label>
            </div>

            {/* View existing attachment via backend (second action icon). Hidden once rejected. */}
            {attachmentId && status !== 'rejected' && typeof onViewAttachment === 'function' && (
              <button
                type="button"
                onClick={() => onViewAttachment(attachmentId, file?.name || initialFileName || sectionName)}
                className="w-6 h-6 text-redhalo-navy bg-redhalo-mint/40 border border-redhalo-teal/50 rounded hover:bg-redhalo-mint/60 flex items-center justify-center"
                title="View Document"
                disabled={disabled}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            )}
            
            {/* Approve/Reject Buttons.
                Show for existing attachments even if local file state is null, so that
                admin can always approve/reject a stored proof. */}
            {(file || attachmentId) && (
              <div className="flex space-x-1">
                <button
                  onClick={handleApprove}
                  disabled={disabled || status === 'approved' || !attachmentId}
                  className={`w-6 h-6 rounded flex items-center justify-center ${
                    status === 'approved' 
                      ? 'bg-green-100 text-green-600 border border-green-200' 
                      : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-green-50 hover:text-green-600'
                  }`}
                  title="Approve"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  onClick={handleReject}
                  disabled={disabled || status === 'rejected' || !attachmentId}
                  className={`w-6 h-6 rounded flex items-center justify-center ${
                    status === 'rejected' 
                      ? 'bg-red-100 text-red-600 border border-red-200' 
                      : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-red-50 hover:text-red-600'
                  }`}
                  title="Reject"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          {infoText && (
            <div className="relative ml-1">
              <button
                type="button"
                className="w-6 h-6 text-gray-600 bg-gray-100 border border-gray-200 rounded flex items-center justify-center hover:bg-gray-200"
                title="More information"
                onClick={() => setShowInfo((prev) => !prev)}
                disabled={disabled}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6a9 9 0 110 18 9 9 0 010-18z" />
                </svg>
              </button>
              {showInfo && (
                <div className="absolute z-20 mt-1 w-64 max-w-xs p-2 bg-white border border-gray-300 shadow-lg text-xs text-gray-700 rounded-none">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-semibold truncate mr-2">{sectionName}</span>
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-800"
                      onClick={() => setShowInfo(false)}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="whitespace-pre-line break-words">{infoText}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Middle: filename/size when pending/approved, or reason when rejected */}
        <div className="flex-1 min-w-0">
          {status === 'rejected' && rejectReason ? (
            <div
              className="text-xs text-red-700 truncate text-center md:text-left"
              title={rejectReason}
            >
              Reason: {rejectReason}
            </div>
          ) : (
            displayName && status !== 'rejected' && (
              <div className="text-xs text-gray-600 truncate text-center md:text-left">
                {displayName}
                {displaySizeBytes
                  ? ` (${(displaySizeBytes / 1024 / 1024).toFixed(2)} MB)`
                  : ''}
              </div>
            )
          )}
        </div>

        {/* Right: status pill only */}
        {status && status !== 'pending' ? (
          <div className="ml-2 flex flex-col items-end max-w-xs">
            <span
              className={`text-xs px-2 py-1 rounded-full ${
                status === 'approved'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {status === 'approved' ? 'Approved' : 'Rejected'}
            </span>
          </div>
        ) : null}
      </div>
      {uploadError && (
        <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-1">
          {uploadError}
        </div>
      )}

      {isRejectModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 w-full max-w-md">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Reject Document</h3>
                <p className="text-xs text-gray-600 mt-1">
                  {sectionName}
                  {file?.name ? ` - ${file.name}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsRejectModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 ml-4"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason for rejection</label>
              <textarea
                value={rejectReasonDraft}
                onChange={(e) => setRejectReasonDraft(e.target.value)}
                rows="3"
                className="block w-full text-sm text-gray-900 bg-transparent border border-gray-300 px-2.5 pb-2.5 pt-2.5 focus:outline-none focus:ring-0 focus:border-redhalo-navy"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setIsRejectModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const trimmed = rejectReasonDraft.trim();
                  // Require a reason, but do not require a local file object for
                  // existing attachments (after reload/tab change we only
                  // have attachmentId & callbacks).
                  if (!trimmed) return;
                  setRejectReason(trimmed);

                  if (attachmentId && typeof onReject === 'function') {
                    try {
                      await onReject({ attachmentId, reason: trimmed, sectionName });
                      setStatus('rejected');
                      setIsRejectModalOpen(false);
                    } catch (err) {
                      console.error('Failed to reject attachment:', err);
                      alert('Failed to reject document. Please try again.');
                    }
                    return;
                  }

                  // Backwards-compatibility: fall back to old onUpload contract if no onReject provided
                  if (typeof onUpload === 'function' && file) {
                    onUpload({
                      sectionName,
                      fileName: file.name,
                      size: file.size,
                      status: 'rejected',
                      rejectReason: trimmed,
                      lastUpdated: new Date().toISOString(),
                      isSectionLevel,
                    });
                    setStatus('rejected');
                    setIsRejectModalOpen(false);
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Modal */}
      {photoModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {photoModal.fileName || 'Document Preview'}
              </h3>
              <button
                onClick={closePhotoModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex justify-center mb-4">
              {photoModal.photo ? (
                <img
                  src={photoModal.photo}
                  alt="Document Preview"
                  className="max-w-full max-h-96 object-contain"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik01MCAxMDAgQzUwIDcyLjM4NTggNzIuMzg1OCA1MCAxMDAgNTAgQzEyNy42MTQgNTAgMTUwIDcyLjM4NTggMTUwIDEwMCBDMTUwIDEyNy42MTQgMTI3LjYxNCAxNTAgMTAwIDE1MCBDNzIuMzg1OCAxNTAgNTAgMTI3LjYxNCA1MCAxMDBaIiBmaWxsPSIjOUI5QkEwIi8+CjxwYXRoIGQ9Ik0xMDAgMTMwIEM4OS4wNTQ0IDEzMCA4MCAxMjAuOTQ1NiA4MCAxMTAgQzgwIDk5LjA1NDQgODkuMDU0NCA5MCAxMDAgOTAgQzExMC45NDYgOTAgMTIwIDk5LjA1NDQgMTIwIDExMCBDMTIwIDEyMC45NDU2IDExMC45NDYgMTMwIDEwMCAxMzBaIiBmaWxsPSIjOUI5QkEwIi8+CjxwYXRoIGQ9Ik0xMDAgMTQwIEM4OS4wNTQ0IDE0MCA4MCAxMzAuOTQ1NiA4MCAxMjAgQzgwIDEwOS4wNTQ0IDg5LjA1NDQgMTAwIDEwMCAxMDAgQzExMC45NDYgMTAwIDEyMCAxMDkuMDU0NCAxMjAgMTIwIEMxMjAgMTMwLjk0NTYgMTEwLjk0NiAxNDAgMTAwIDE0MFoiIGZpbGw9IiM5QjlCQTAiLz4KPC9zdmc+';
                  }}
                />
              ) : (
                <div className="w-48 h-48 bg-gray-100 flex items-center justify-center">
                  <svg className="w-24 h-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
              )}
            </div>
            
            <div className="flex justify-center space-x-4">
              {photoModal.photo && (
                <button
                  onClick={downloadPhoto}
                  className="px-4 py-2 rh-btn-primary rounded-md hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-2 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download</span>
                </button>
              )}
              <button
                onClick={closePhotoModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --------------------- BulkActions Component ---------------------
const BulkActions = ({ sectionName, onBulkAction, disabled = false }) => {
  const handleBulkApprove = () => {
    onBulkAction(sectionName, 'approve');
  };

  const handleBulkReject = () => {
    const reason = prompt('Enter reason for rejection:');
    if (reason) {
      onBulkAction(sectionName, 'reject', reason);
    }
  };

  return (
    <div className="mt-2 p-2 border border-gray-200 bg-redhalo-mint/40">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">Bulk Actions:</span>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleBulkApprove}
            disabled={disabled}
            className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:bg-gray-400"
          >
            ✓ Approve All
          </button>
          <button
            onClick={handleBulkReject}
            disabled={disabled}
            className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 disabled:bg-gray-400"
          >
            ✗ Reject All
          </button>
        </div>
      </div>
    </div>
  );
};

// --------------------- InvestmentDeclarationModal ---------------------
export default function InvestmentDeclarationModal({
  employee,
  mode,
  onClose,
  onSave,
  declarationHeader,
  attachments,
  payrollSettings,
  isInvestmentDeclarationEnabled,
  isInvestmentProofEnabled,
  getInvestmentDeclarationMode,
  getInvestmentProofMode,
  financialYear,
}) {
  console.debug('🔍 InvestmentDeclarationModal rendered with:', {
    employee: employee?.id,
    mode,
    financialYear,
    payrollSettings: payrollSettings ? 'present' : 'missing'
  });
  const [activeTab, setActiveTab] = useState('hra');
  const [selectedTaxRegime, setSelectedTaxRegime] = useState(() => {
    const empRegime = employee?.taxRegime;
    if (!empRegime) return 'new';
    return empRegime.toString().toLowerCase() === 'old' ? 'old' : 'new';
  });
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [declarationData, setDeclarationData] = useState({
    houseRent: {
      cityType: '',
      rentPaid: '',
      periods: [],
      landlordPan: '',
      address: '',
    },
    hraEntries: [],
    section80C: {
      lifeInsurance: '',
      ppf: '',
      vpf: '',
      pensionFund: '',
      nsc: '',
      nscInterest: '',
      ulip: '',
      elss: '',
      tuitionFees: '',
      houseLoanPrincipal: '',
      registrationCharges: '',
      sukanyaSamriddhi: '',
      infrastructureBonds: '',
      fixedDeposit: '',
    },
    sectionVIA: {
      section80D: {
        self: '',
        parents: '',
        parentsSenior: '',
        isSelfSeniorCitizen: false,
        isParentsSeniorCitizen: false,
        preventiveHealthCheckup: '',
      },
      otherInvestments: {
        section80E: '',
        section80CCD1B: '',
        section80EE: '',
        section80EEA: '',
        section80EEB: '',
        section80G: '',
        section80TTA: '',
        section80TTB: '',
      },
    },
    houseProperty: {
      propertyType: '',
      loanTakenBeforeApril1999: false,
      interest: '',
      rentalIncome: '',
      address: '',
    },
    housePropertyEntries: [],
  });

  const getCurrentFinancialYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    return month >= 4 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
  };

  const getEffectiveFinancialYear = () => {
    return financialYear || declarationHeader?.financialYear || getCurrentFinancialYear();
  };

  // Previous Employment header fields
  const [prevEmployment, setPrevEmployment] = useState({
    employerName: declarationHeader?.prevEmployerName?.toString() || '',
    grossSalary:
      declarationHeader?.prevGrossSalary != null
        ? declarationHeader.prevGrossSalary.toString()
        : '',
    taxDeducted:
      declarationHeader?.prevTaxDeducted != null
        ? declarationHeader.prevTaxDeducted.toString()
        : '',
  });

  useEffect(() => {
    if (!declarationHeader) return;
    setPrevEmployment({
      employerName: declarationHeader.prevEmployerName?.toString() || '',
      grossSalary:
        declarationHeader.prevGrossSalary != null
          ? declarationHeader.prevGrossSalary.toString()
          : '',
      taxDeducted:
        declarationHeader.prevTaxDeducted != null
          ? declarationHeader.prevTaxDeducted.toString()
          : '',
    });
    const overrideState = {
      allowAllEdits: !!declarationHeader.allowAllEdits,
      allowPrevEmploymentEdit: !!declarationHeader.allowPrevEmploymentEdit,
      allowHraEdit: !!declarationHeader.allowHraEdit,
      allowSection80CEdit: !!declarationHeader.allowSection80CEdit,
      allowViaEdit: !!declarationHeader.allowViaEdit,
      allowHousePropertyEdit: !!declarationHeader.allowHousePropertyEdit,
    };
    setAllowAllEdits(overrideState.allowAllEdits);
    setAllowPrevEmploymentEdit(overrideState.allowPrevEmploymentEdit);
    setAllowHraEdit(overrideState.allowHraEdit);
    setAllowSection80CEdit(overrideState.allowSection80CEdit);
    setAllowViaEdit(overrideState.allowViaEdit);
    setAllowHousePropertyEdit(overrideState.allowHousePropertyEdit);
    // Also update the original state
    setOriginalOverrideState(overrideState);
  }, [
    declarationHeader?.prevEmployerName,
    declarationHeader?.prevGrossSalary,
    declarationHeader?.prevTaxDeducted,
    declarationHeader?.allowAllEdits,
    declarationHeader?.allowPrevEmploymentEdit,
    declarationHeader?.allowHraEdit,
    declarationHeader?.allowSection80CEdit,
    declarationHeader?.allowViaEdit,
    declarationHeader?.allowHousePropertyEdit,
  ]);

  const [errors, setErrors] = useState({});
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showSubmitSuccessModal, setShowSubmitSuccessModal] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState({ open: false, mode: null }); // mode: 'old' | 'new'
  const [cancelConfirmMode, setCancelConfirmMode] = useState(null); // 'old_nothing_declared' | 'new' | null
  const initialSelectedTaxRegimeRef = useRef(null); // regime when modal was loaded; cancel popup only when user changed regime (old↔new)
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [savedSections, setSavedSections] = useState(new Set());
  const darkMode = false;

  // Admin override flags to reopen sections for editing in employee view
  const [allowAllEdits, setAllowAllEdits] = useState(!!declarationHeader?.allowAllEdits);
  const [allowPrevEmploymentEdit, setAllowPrevEmploymentEdit] = useState(
    !!declarationHeader?.allowPrevEmploymentEdit
  );
  const [allowHraEdit, setAllowHraEdit] = useState(!!declarationHeader?.allowHraEdit);
  const [allowSection80CEdit, setAllowSection80CEdit] = useState(
    !!declarationHeader?.allowSection80CEdit
  );
  const [allowViaEdit, setAllowViaEdit] = useState(!!declarationHeader?.allowViaEdit);
  const [allowHousePropertyEdit, setAllowHousePropertyEdit] = useState(
    !!declarationHeader?.allowHousePropertyEdit
  );

  // Original override state - tracks the state that was last saved/loaded from backend
  // This is what gets used when saving form sections (HRA, etc.)
  const [originalOverrideState, setOriginalOverrideState] = useState({
    allowAllEdits: !!declarationHeader?.allowAllEdits,
    allowPrevEmploymentEdit: !!declarationHeader?.allowPrevEmploymentEdit,
    allowHraEdit: !!declarationHeader?.allowHraEdit,
    allowSection80CEdit: !!declarationHeader?.allowSection80CEdit,
    allowViaEdit: !!declarationHeader?.allowViaEdit,
    allowHousePropertyEdit: !!declarationHeader?.allowHousePropertyEdit,
  });

  // When allowAllEdits is turned on, auto-enable all section flags so
  // the UI reflects that everything is open. When turned off, leave
  // section flags as-is so admin can fine-tune.
  useEffect(() => {
    if (allowAllEdits) {
      setAllowPrevEmploymentEdit(true);
      setAllowHraEdit(true);
      setAllowSection80CEdit(true);
      setAllowViaEdit(true);
      setAllowHousePropertyEdit(true);
    }
  }, [allowAllEdits]);

  const setFieldError = (key, message) => {
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });
  };

  const hasProof = (sectionName) => {
    const att = getFieldAttachment(sectionName);
    return !!(att && att.attachmentId);
  };

  const getPrevEmploymentExperiences = () =>
    employeeProfile?.experiences ?? employee?.experiences ?? [];

  const getPrevEmploymentIsFresher = () =>
    employeeProfile?.isFresher ?? employee?.isFresher;

  const requiresPrevEmploymentDetails = () =>
    employeeRequiresPrevEmploymentDetails({
      experiences: getPrevEmploymentExperiences(),
      isFresher: getPrevEmploymentIsFresher(),
    });

  const buildPrevEmploymentHeaderPart = () =>
    normalizePrevEmploymentForSave(prevEmployment, {
      applyDefaultsIfEmpty: !requiresPrevEmploymentDetails(),
    });

  const validateCurrentSection = (sectionId) => {
    const nextErrors = {};

    if (sectionId === 'prevEmployment') {
      Object.assign(
        nextErrors,
        validatePrevEmploymentFields(prevEmployment, {
          required: requiresPrevEmploymentDetails(),
        })
      );
    }

    if (sectionId === 'hra') {
      const rows = declarationData.hraEntries || [];
      for (let i = 0; i < rows.length; i++) {
        const rentKey = `hra-rentPaid-${i}`;
        const panKey = `hra-landlordPan-${i}`;
        const addrKey = `hra-address-${i}`;
        const rentErr = getNumberRangeError(`Total Rent Paid `, rows[i]?.totalRentPaid);
        const panErr = getPanError(`Landlord PAN`, rows[i]?.landlordPan);
        const addrErr = getAddressError(`Rental Address `, rows[i]?.rentalAddress);
        if (rentErr) nextErrors[rentKey] = rentErr;
        if (panErr) nextErrors[panKey] = panErr;
        if (addrErr) nextErrors[addrKey] = addrErr;

        const amount = Number(rows[i]?.totalRentPaid) || 0;
        
        // If total rent > 1 lakh (100000), landlord PAN is mandatory
        if (amount > 100000) {
          const landlordPan = (rows[i]?.landlordPan || '').trim();
          if (!landlordPan) {
            nextErrors[panKey] = `Landlord PAN is mandatory when Total Rent Paid is more than ₹1,00,000`;
          } else {
            // Still validate PAN format if provided
            const panValidationErr = getPanError(`Landlord PAN`, landlordPan);
            if (panValidationErr) {
              nextErrors[panKey] = panValidationErr;
            }
          }
        }
        
        // Only show proof document error if proof upload is enabled and not expired/hidden
        if (amount > 0 && !hasProof(`HRA - Entry ${i + 1}`) && isInvestmentProofEnabled && !isProofUploadDisabled()) {
          nextErrors[rentKey] =
            nextErrors[rentKey] ||
            'Proof document is required when Total Rent Paid/Payable is entered.';
        }
      }
    }

    if (sectionId === 'section80C') {
      const s = declarationData.section80C || {};
      const map = [
        [
          'lifeInsurance',
          'Life Insurance',
          s.lifeInsurance,
          'Life Insurance Premium',
        ],
        [
          'ppf',
          'PPF',
          s.ppf,
          'Public Provident Fund (PPF)',
        ],
        [
          'vpf',
          'VPF',
          s.vpf,
          'Voluntary Provident Fund (VPF)',
        ],
        [
          'pensionFund',
          'Pension Fund',
          s.pensionFund,
          'Pension Fund Contribution',
        ],
        [
          'nsc',
          'NSC',
          s.nsc,
          'National Savings Certificate (NSC)',
        ],
        [
          'nscInterest',
          'NSC Interest',
          s.nscInterest,
          'Interest Accrued on NSC (Reinvested)',
        ],
        [
          'ulip',
          'ULIP',
          s.ulip,
          'Unit Linked Insurance Policy (ULIP)',
        ],
        [
          'elss',
          'ELSS',
          s.elss,
          'Equity Linked Savings Schemes (ELSS)',
        ],
        [
          'tuitionFees',
          'Tuition Fees',
          s.tuitionFees,
          'Payment of Tuition Fees (Max 2 Children)',
        ],
        [
          'houseLoanPrincipal',
          'House Loan Principal',
          s.houseLoanPrincipal,
          'Principal Repayment of Housing Loan',
        ],
        [
          'registrationCharges',
          'Registration Charges',
          s.registrationCharges,
          'Registration Charges for Buying House',
        ],
        [
          'sukanyaSamriddhi',
          'Sukanya Samriddhi',
          s.sukanyaSamriddhi,
          'Sukanya Samriddhi Yojana',
        ],
        [
          'infrastructureBonds',
          'Infrastructure Bonds',
          s.infrastructureBonds,
          'Infrastructure Bonds',
        ],
        [
          'fixedDeposit',
          'Fixed Deposit',
          s.fixedDeposit,
          'Bank Fixed Deposit (5+ Years)',
        ],
      ];
      for (const [key, label, value, proofLabel] of map) {
        const err = getNumberRangeError(label, value);
        if (err) nextErrors[key] = err;

        const amount = Number(value) || 0;
        // Only show proof document error if proof upload is enabled and not expired/hidden
        if (amount > 0 && !hasProof(proofLabel) && isInvestmentProofEnabled && !isProofUploadDisabled()) {
          nextErrors[key] =
            nextErrors[key] ||
            `Proof document is required when ${label} is entered.`;
        }
      }
    }

    if (sectionId === 'viaDeductions') {
      const d = declarationData.sectionVIA?.section80D || {};
      const o = declarationData.sectionVIA?.otherInvestments || {};
      const map = [
        [
          'section80DSelf',
          'Self Medical Insurance',
          d.self,
          'Section 80D - Medical Insurance',
        ],
        [
          'section80DParents',
          'Parents Medical Insurance',
          d.parents,
          'Section 80D - Medical Insurance',
        ],
        [
          'section80DParentsSenior',
          'Senior Citizen Parents Medical Insurance',
          d.parentsSenior,
          'Section 80D - Medical Insurance',
        ],
        [
          'preventiveHealthCheckup',
          'Preventive Health Checkup',
          d.preventiveHealthCheckup,
          'Section 80D - Medical Insurance',
        ],
        [
          'section80E',
          'Interest Education Loan (80E)',
          o.section80E,
          'Interest on Education Loan (80E)',
        ],
        [
          'section80CCD1B',
          'NPS Additional Contribution (80CCD(1B))',
          o.section80CCD1B,
          'NPS Additional Contribution (80CCD(1B))',
        ],
        [
          'section80EE',
          'Interest on Home Loan (80EE)',
          o.section80EE,
          'Interest on Home Loan (80EE)',
        ],
        [
          'section80EEA',
          'Interest on Home Loan (80EEA)',
          o.section80EEA,
          'Interest on Home Loan (80EEA)',
        ],
        [
          'section80EEB',
          'Interest on Home Loan (80EEB)',
          o.section80EEB,
          'Interest on Home Loan (80EEB)',
        ],
        [
          'section80G',
          'Donations (80G)',
          o.section80G,
          'Donations (80G)',
        ],
        [
          'section80TTA',
          'Interest on Savings (80TTA)',
          o.section80TTA,
          'Interest on Savings Account (80TTA)',
        ],
        [
          'section80TTB',
          'Interest on Deposits (80TTB)',
          o.section80TTB,
          'Interest on Deposits (80TTB)',
        ],
      ];
      for (const [key, label, value, proofLabel] of map) {
        const err = getNumberRangeError(label, value);
        if (err) nextErrors[key] = err;

        const amount = Number(value) || 0;
        // Only show proof document error if proof upload is enabled and not expired/hidden
        if (amount > 0 && !hasProof(proofLabel) && isInvestmentProofEnabled && !isProofUploadDisabled()) {
          nextErrors[key] =
            nextErrors[key] ||
            `Proof document is required when ${label} is entered.`;
        }
      }
    }

    if (sectionId === 'houseProperty') {
      const rows = declarationData.housePropertyEntries || [];
      for (let i = 0; i < rows.length; i++) {
        const key = `interest-${i}`;
        const err = getNumberRangeError(
          `Interest Paid on Borrowed Capital `,
          rows[i]?.interest
        );
        if (err) nextErrors[key] = err;

        const amount = Number(rows[i]?.interest) || 0;
        // Only show proof document error if proof upload is enabled and not expired/hidden
        if (amount > 0 && !hasProof(`House Property - Entry ${i + 1}`) && isInvestmentProofEnabled && !isProofUploadDisabled()) {
          nextErrors[key] =
            nextErrors[key] ||
            'Proof document is required when Interest Paid on Borrowed Capital is entered.';
        }
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Global attachment preview state (reuse employee behavior)
  const [attachmentPreview, setAttachmentPreview] = useState({
    isOpen: false,
    url: null,
    downloadUrl: null,
    fileName: '',
  });

  const attachmentUrlCacheRef = useRef(new Map());
  const attachmentDownloadUrlCacheRef = useRef(new Map());

  const openAttachment = async (attachmentId, fileName) => {
    if (!attachmentId) return;
    try {
      const cached = attachmentUrlCacheRef.current.get(attachmentId);
      const cachedDownload = attachmentDownloadUrlCacheRef.current.get(attachmentId);
      if (cached) {
        setAttachmentPreview({
          isOpen: true,
          url: cached,
          downloadUrl: cachedDownload || null,
          fileName: fileName || 'Document',
        });
        return;
      }

      const [previewResp, downloadResp] = await Promise.all([
        getInvestmentAttachmentUrl(attachmentId),
        getInvestmentAttachmentDownloadUrl(attachmentId),
      ]);
      const url = previewResp?.data?.url || previewResp?.url || previewResp;
      const downloadUrl =
        downloadResp?.data?.url || downloadResp?.url || downloadResp;
      if (url) {
        attachmentUrlCacheRef.current.set(attachmentId, url);
        if (downloadUrl) {
          attachmentDownloadUrlCacheRef.current.set(attachmentId, downloadUrl);
        }
        setAttachmentPreview({
          isOpen: true,
          url,
          downloadUrl: downloadUrl || null,
          fileName: fileName || 'Document',
        });
      } else {
        toast.error('Unable to open document: no URL returned');
      }
    } catch (err) {
      console.error('Failed to open attachment', err);
      toast.error('Failed to open document. Please try again.');
    }
  };
  // Helper functions to determine field states based on payroll settings
  const isInvestmentDeclarationReadOnly = () => {
    
    // Temporary fix: If no payroll settings, make fields editable for testing
    if (!payrollSettings) {
      return false;
    }
    
    if (!isInvestmentDeclarationEnabled) {
      return true;
    }
    
    const mode = getInvestmentDeclarationMode?.() || 'manual';
    
    if (mode === 'always') {
      return false; // Always editable
    }
    
    if (mode === 'auto') {
      // Check if current date is within the investment declaration window
      const withinWindow = isWithinInvestmentWindow();
      return !withinWindow;
    }
    
    // Manual mode - check if within window
    const withinWindow = isWithinInvestmentWindow();
    return !withinWindow;
  };

  const isProofUploadReadOnly = () => {
    
    // Temporary fix: If no payroll settings, make proof upload editable for testing
    if (!payrollSettings) {
      return false;
    }
    
    if (!isInvestmentProofEnabled) {
      return true;
    }
    
    const mode = getInvestmentProofMode?.() || 'manual';
    
    if (mode === 'always') {
      return false; // Always editable
    }
    
    if (mode === 'auto') {
      // Check if current date is within the proof upload window
      const withinWindow = isWithinProofWindow();
      return !withinWindow;
    }
    
    // Manual mode - check if within window
    const withinWindow = isWithinProofWindow();
    return !withinWindow;
  };

  const isProofUploadDisabled = () => {
    // Returns true if proof upload is disabled (read-only or expired/hidden)
    return isProofUploadReadOnly();
  };

  const isTaxRegimeLocked = () => {
    return payrollSettings?.isTaxRegimeLocked || false;
  };

  // Helper functions to check if current date is within windows
  const isWithinInvestmentWindow = () => {
    
    if (!payrollSettings) {
      return false;
    }
    
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-11
    const currentDay = today.getDate();
    
    
    if (payrollSettings.investmentMode === 'AUTO_MONTHLY') {
      // Auto mode - check if within monthly window (applies to every month)
      const fromDay = parseInt(payrollSettings.investmentFromDay) || 1;
      const toDay = parseInt(payrollSettings.investmentToDay) || 31;
      
      // Use currentYear and currentMonth for proper date validation
      console.debug(`🔍 Investment Window: ${currentYear}-${currentMonth + 1}, Day ${currentDay} (range: ${fromDay}-${toDay})`);
      
      const result = currentDay >= fromDay && currentDay <= toDay;
      return result;
    } else {
      // Manual mode - check if before manual end date
      const endDate = payrollSettings.investmentEndDate;
      
      if (endDate) {
        const endDateTime = new Date(endDate);
        // Treat manual investment end date as inclusive for the entire calendar day
        endDateTime.setHours(23, 59, 59, 999);
        const result = today <= endDateTime;
        return result;
      }
      return false;
    }
  };

  const isWithinProofWindow = () => {
    
    if (!payrollSettings) {
      return false;
    }
    
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // 1-12
    const currentDay = today.getDate();
    
    
    if (payrollSettings.proofMode === 'AUTO_YEARLY') {
      // Auto mode - check if within yearly window for specific month
      const proofMonth = parseInt(payrollSettings.proofMonth) || 1; // 1-12
      const fromDay = parseInt(payrollSettings.proofFromDay) || 1;
      const toDay = parseInt(payrollSettings.proofToDay) || 31;
      
      // Use currentYear and currentMonth for proper date validation
      console.debug(`🔍 Proof Window: ${currentYear}, Month ${currentMonth} (target: ${proofMonth}), Day ${currentDay} (range: ${fromDay}-${toDay})`);
      
      // Check if current month matches proof month
      if (currentMonth === proofMonth) {
        const result = currentDay >= fromDay && currentDay <= toDay;
        return result;
      }
      return false;
    } else {
      // Manual mode - check if before manual end date
      const endDate = payrollSettings.proofEndDate;
      
      if (endDate) {
        const endDateTime = new Date(endDate);
        // Treat manual proof end date as inclusive for the entire calendar day
        endDateTime.setHours(23, 59, 59, 999);
        const result = today <= endDateTime;
        return result;
      }
      return false;
    }
  };

  // --------------------- NEW: For Proof Documents ---------------------
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [declarationId, setDeclarationId] = useState(declarationHeader?.id || null);

  useEffect(() => {
    if (declarationHeader?.id) {
      setDeclarationId(declarationHeader.id);
    }
  }, [declarationHeader?.id]);

  // Initialize default HRA and House Property entries if they don't exist
  useEffect(() => {
    if (!declarationData.hraEntries || declarationData.hraEntries.length === 0) {
      setDeclarationData(prev => ({
        ...prev,
        hraEntries: [{
          cityType: 'metro',
          totalRentPaid: '',
          landlordPan: '',
          rentalAddress: '',
        }]
      }));
    }
    
    if (!declarationData.housePropertyEntries || declarationData.housePropertyEntries.length === 0) {
      setDeclarationData(prev => ({
        ...prev,
        housePropertyEntries: [{
          propertyType: 'self-occupied',
          loanTakenBeforeApril1999: false,
          interest: '',
          rentalIncome: '',
          address: '',
        }]
      }));
    }
  }, []);

  const getFieldAttachment = (sectionName) => {
    const normalize = (name) => {
      if (!name) return '';
      let n = name.toString().toLowerCase().trim();
      // Backwards-compatibility: older data may use longer HRA label
      n = n.replace('house rent allowance (hra) - ', 'hra - ');
      // Normalize various 80D labels to a single key
      n = n.replace('self medical insurance (80d)', 'section 80d - medical insurance');
      n = n.replace('section 80d', 'section 80d - medical insurance');
      // Normalize NPS 80CCD(1B) label variants
      n = n.replace('section 80ccd(1b)', 'nps additional contribution (80ccd(1b))');
      // Normalize 80TTA variations
      n = n.replace('interest on savings (80tta)', 'interest on savings account (80tta)');
      n = n.replace('interest on savings account (80tta)', 'interest on savings account (80tta)');
      // Normalize 80TTB variations
      n = n.replace('interest on deposits (80ttb)', 'interest on deposits (80ttb)');
      return n;
    };

    const target = normalize(sectionName);

    const match = uploadedDocuments.find((doc) => normalize(doc.documentName) === target);

    // If we have a match but it somehow lacks fileName (older data, or mapping
    // was missing), derive a filename from fileUrl as a last-resort fallback
    // so callers like SectionUpload always see a stable name.
    const hydratedMatch =
      match && !match.fileName && match.fileUrl
        ? {
            ...match,
            fileName: (match.fileUrl || '').split('/').pop() || undefined,
          }
        : match;

    return hydratedMatch;
  };

  useEffect(() => {
    if (Array.isArray(attachments)) {
      const mapped = attachments.map((att) => {
        const pathName = (att.filePath || '').split('/').pop() || undefined;
        const effectiveFileName =
          att.originalFileName || att.downloadFileName || att.fileName || pathName;

        return {
          documentName: att.sectionName || att.fieldName || 'Document',
          // Use effectiveFileName for UI display, deriving it from filePath when
          // original/download/fileName are missing (older data).
          fileName: effectiveFileName,
          // Expose explicit original/download names for consumers that care
          originalFileName: effectiveFileName,
          downloadFileName: att.downloadFileName || null,
          size: att.fileSize,
          status: (att.status || 'PENDING').toLowerCase(),
          lastUpdated: att.uploadedAt || att.reviewedAt || new Date().toISOString(),
          comment: att.comments || '',
          isSectionLevel: !att.fieldName,
          attachmentId: att.id,
          fileUrl: att.filePath,
        };
      });
      setUploadedDocuments(mapped);
    }
  }, [attachments]);

  // Fetch selected employee's investment declaration for admin view
  useEffect(() => {
    const loadInvestment = async () => {
      if (!employee?.id) return;
      try {
        const resp = await payrollService.getInvestmentData(employee.id, financialYear);
        if (resp?.success && resp.data) {
          const { header, hra, section80c, via, houseProperty, attachments: atts } = resp.data;
          if (header?.id) setDeclarationId(header.id);
          setPrevEmployment({
            employerName: header?.prevEmployerName?.toString() || '',
            grossSalary:
              header?.prevGrossSalary != null ? header.prevGrossSalary.toString() : '',
            taxDeducted:
              header?.prevTaxDeducted != null ? header.prevTaxDeducted.toString() : '',
          });
          // Sync tax regime selection with backend header if present; track initial for cancel popup (only when regime changed)
          if (header?.taxRegime) {
            const regime = header.taxRegime.toString().toLowerCase() === 'old' ? 'old' : 'new';
            setSelectedTaxRegime(regime);
            initialSelectedTaxRegimeRef.current = regime;
          } else {
            const empRegime = employee?.taxRegime?.toString().toLowerCase() === 'old' ? 'old' : 'new';
            initialSelectedTaxRegimeRef.current = empRegime;
          }
          // Rules handling (defensive)
          const rules = resp.data.rules || {};
          const inv = rules.investment || {};
          const prf = rules.proof || {};
          const tax = rules.taxRegime || {};
          // Note: Investment declaration, proof upload, and tax regime rules are now handled by payroll settings props
          // The old state-based approach has been replaced with prop-based helper functions

          const mappedHra =
            Array.isArray(hra) && hra.length
              ? hra.map((row) => ({
                  cityType: row.isMetro ? 'metro' : 'non-metro',
                  totalRentPaid: row.totalRentPaid?.toString() ?? '',
                  landlordPan: row.landlordPan ?? '',
                  rentalAddress: row.rentalAddress ?? '',
                }))
              : [
                  {
                    cityType: 'metro',
                    totalRentPaid: '',
                    landlordPan: '',
                    rentalAddress: '',
                  },
                ];

          const mappedHouseProperty =
            Array.isArray(houseProperty) && houseProperty.length
              ? houseProperty.map((row) => ({
                  // Map backend occupancy to old UI's propertyType field
                  propertyType:
                    (row.occupancy || 'SELF_OCCUPIED') === 'LET_OUT'
                      ? 'let-out'
                      : 'self-occupied',
                  loanTakenBeforeApril1999: !!row.loanBeforeApr1999,
                  // Old UI uses `interest` string field
                  interest: row.interestPaidOnBorrowedCapital?.toString() ?? '',
                  // Old UI also had optional rentalIncome; backend currently doesn't store it
                  rentalIncome: '',
                  address: row.propertyAddress ?? '',
                }))
              : [
                  {
                    propertyType: 'self-occupied',
                    loanTakenBeforeApril1999: false,
                    interest: '',
                    rentalIncome: '',
                    address: '',
                  },
                ];

          // Initialize override flags from the live header so the
          // Employee Access Overrides checkboxes reflect DB values.
          if (header) {
            const overrideState = {
              allowAllEdits: !!header.allowAllEdits,
              allowPrevEmploymentEdit: !!header.allowPrevEmploymentEdit,
              allowHraEdit: !!header.allowHraEdit,
              allowSection80CEdit: !!header.allowSection80CEdit,
              allowViaEdit: !!header.allowViaEdit,
              allowHousePropertyEdit: !!header.allowHousePropertyEdit,
            };
            setAllowAllEdits(overrideState.allowAllEdits);
            setAllowPrevEmploymentEdit(overrideState.allowPrevEmploymentEdit);
            setAllowHraEdit(overrideState.allowHraEdit);
            setAllowSection80CEdit(overrideState.allowSection80CEdit);
            setAllowViaEdit(overrideState.allowViaEdit);
            setAllowHousePropertyEdit(overrideState.allowHousePropertyEdit);
            // Also update the original state (what gets used when saving form sections)
            setOriginalOverrideState(overrideState);
          }

          setDeclarationData((prev) => ({
            ...prev,
            hraEntries: mappedHra,
            section80C: {
              ...prev.section80C,
              lifeInsurance:
                section80c?.lifeInsurancePremium?.toString() || prev.section80C.lifeInsurance,
              ppf: section80c?.ppf?.toString() || prev.section80C.ppf,
              vpf: section80c?.vpf?.toString() || prev.section80C.vpf,
              pensionFund:
                section80c?.pensionFund?.toString() || prev.section80C.pensionFund,
              nsc: section80c?.nsc?.toString() || prev.section80C.nsc,
              nscInterest:
                section80c?.nscInterestReinvested?.toString() || prev.section80C.nscInterest,
              ulip: section80c?.ulip?.toString() || prev.section80C.ulip,
              elss: section80c?.elss?.toString() || prev.section80C.elss,
              tuitionFees:
                section80c?.tuitionFees?.toString() || prev.section80C.tuitionFees,
              houseLoanPrincipal:
                section80c?.principalHousingLoan?.toString() ||
                prev.section80C.houseLoanPrincipal,
              registrationCharges:
                section80c?.registrationCharges?.toString() ||
                prev.section80C.registrationCharges,
              sukanyaSamriddhi:
                section80c?.sukanyaSamriddhi?.toString() ||
                prev.section80C.sukanyaSamriddhi,
              infrastructureBonds:
                section80c?.infrastructureBonds?.toString() ||
                prev.section80C.infrastructureBonds,
              fixedDeposit:
                section80c?.bankFixedDeposit?.toString() || prev.section80C.fixedDeposit,
            },
            sectionVIA: {
              ...prev.sectionVIA,
              section80D: {
                ...prev.sectionVIA.section80D,
                self:
                  via?.selfMedicalInsurance?.toString() ||
                  prev.sectionVIA.section80D.self,
                parents:
                  via?.parentsMedicalInsurance?.toString() ||
                  prev.sectionVIA.section80D.parents,
                parentsSenior:
                  via?.seniorCitizenParentsMedical?.toString() ||
                  prev.sectionVIA.section80D.parentsSenior,
                isParentsSeniorCitizen:
                  (parseFloat(via?.seniorCitizenParentsMedical || '0') > 0) ||
                  prev.sectionVIA.section80D.isParentsSeniorCitizen,
                preventiveHealthCheckup:
                  via?.preventiveHealthCheckup?.toString() ||
                  prev.sectionVIA.section80D.preventiveHealthCheckup,
              },
              otherInvestments: {
                ...prev.sectionVIA.otherInvestments,
                section80E:
                  via?.interestEducationLoan?.toString() ||
                  prev.sectionVIA.otherInvestments.section80E,
                section80CCD1B:
                  via?.npsAdditionalContribution?.toString() ||
                  prev.sectionVIA.otherInvestments.section80CCD1B,
                section80EE:
                  via?.interestOnHomeLoan80EE?.toString() ||
                  prev.sectionVIA.otherInvestments.section80EE,
                section80EEA:
                  via?.interestOnHomeLoan80EEA?.toString() ||
                  prev.sectionVIA.otherInvestments.section80EEA,
                section80EEB:
                  via?.interestOnHomeLoan80EEB?.toString() ||
                  prev.sectionVIA.otherInvestments.section80EEB,
                section80G:
                  via?.donations80G?.toString() ||
                  prev.sectionVIA.otherInvestments.section80G,
                section80TTA:
                  via?.interestOnSavings80TTA?.toString() ||
                  prev.sectionVIA.otherInvestments.section80TTA,
                section80TTB:
                  via?.interestOnDeposits80TTB?.toString() ||
                  prev.sectionVIA.otherInvestments.section80TTB,
              },
            },
            housePropertyEntries: mappedHouseProperty,
          }));

          if (!Array.isArray(attachments) && Array.isArray(atts)) {
            const mapped = atts.map((att) => ({
              documentName: att.sectionName || att.fieldName || 'Document',
              fileName: att.fileName,
              size: att.fileSize,
              status: (att.status || 'PENDING').toLowerCase(),
              lastUpdated: att.uploadedAt || att.reviewedAt || new Date().toISOString(),
              comment: att.comments || '',
              isSectionLevel: !att.fieldName,
              attachmentId: att.id,
              fileUrl: att.filePath,
            }));
            setUploadedDocuments(mapped);
          }
        } else {
          // 200 with success: false and "Investment declaration not found for given employee/year" – no toast
          const msg = (resp?.message || '').toLowerCase();
          if (!msg.includes('investment declaration not found')) {
            toast.error('Failed to load investment declaration data. Please try again.');
          }
        }
      } catch (e) {
        // 404 or response body "Investment declaration not found for given employee/year" – don't show errors
        if (e?.response?.status === 404) return;
        const errorMessage = e?.response?.data?.message || e?.message || '';
        if (errorMessage.toLowerCase().includes('investment declaration not found')) return;
        console.error('Failed to load investment data for admin:', e);
        toast.error('Failed to load investment declaration data. Please try again.');
      }
    };
    loadInvestment();
  }, [employee?.id, financialYear]);

  // Load full employee profile so experiences/isFresher can drive Previous Employment tab visibility
  useEffect(() => {
    const loadEmployeeProfile = async () => {
      if (!employee?.id) {
        setEmployeeProfile(null);
        return;
      }
      try {
        const resp = await employeeService.getEmployee(employee.id);
        setEmployeeProfile(resp?.data || null);
      } catch (e) {
        console.error('Failed to load employee profile for investment modal:', e);
        toast.error('Failed to load employee profile. Please try again.');
        setEmployeeProfile(null);
      }
    };
    loadEmployeeProfile();
  }, [employee?.id]);

  // Reset initial tax regime ref when modal closes so next open doesn't use stale value
  useEffect(() => {
    if (!employee?.id) initialSelectedTaxRegimeRef.current = null;
  }, [employee?.id]);

  // Enforce tax regime lock based on payroll settings
  useEffect(() => {
    if (isTaxRegimeLocked() && employee?.taxRegime) {
      // If tax regime is locked, force the selection to match employee's tax regime
      const empRegime = employee.taxRegime.toString().toLowerCase();
      setSelectedTaxRegime(empRegime === 'old' ? 'old' : 'new');
    }
  }, [employee?.taxRegime, payrollSettings]);

  const ensureDeclarationId = async () => {
    if (declarationId) return declarationId;

    if (!employee?.id) {
      throw new Error('Employee ID is not available for initializing declaration');
    }

    const fy = getEffectiveFinancialYear();

    const json = await payrollService.saveInvestmentData(employee.id, {
      financialYear: fy,
      taxRegime: selectedTaxRegime === 'old' ? 'OLD' : 'NEW',
    });
    const newId = json?.data?.header?.id;
    if (!newId) {
      throw new Error('Declaration header ID missing in response');
    }

    setDeclarationId(newId);
    return newId;
  };

  const handleAdminDocumentUpload = async ({ file, sectionName, isSectionLevel }, sectionId) => {
    const effectiveDeclarationId = await ensureDeclarationId();

    // Use employee.id/empCode and sectionName to build deterministic R2 keys and download names
    const employeeId = employee?.id;
    const empCode = employee?.empCode || employee?.employeeCode || employee?.code || '';
    const slotKey = sectionId != null ? `${sectionName}-${sectionId}` : sectionName;

    // Derive extension from the uploaded file name
    const dotIndex = file.name.lastIndexOf('.');
    const ext = dotIndex !== -1 ? file.name.substring(dotIndex) : '';

    // R2 storage file name should be label-based, e.g. "Life Insurance Premium.pdf"
    const storageFileName = `${sectionName}${ext}`;

    const storageInfo = await uploadFileToR2(file, {
      employeeId,
      slotKey,
      storageFileName,
    });

    // Download file name in DB should be like PS0001_Life_Insurance_Premium.pdf
    const safeLabel = sectionName.replace(/[^A-Za-z0-9]+/g, '_');
    const downloadFileName = empCode
      ? `${empCode}_${safeLabel}${ext}`
      : `${safeLabel}${ext}`;

    const attachment = await createInvestmentAttachment(effectiveDeclarationId, {
      sectionName,
      fieldName: isSectionLevel ? null : sectionName,
      r2Path: storageInfo.r2Path,
      fileUrl: storageInfo.fileUrl,
      fileName: storageInfo.fileName || file.name,
      fileSize: storageInfo.fileSize || file.size,
      originalFileName: file.name,
      downloadFileName,
    });

    // Invalidate any cached signed URL for this attachment so preview/download always loads the latest file.
    if (attachment?.id) {
      attachmentUrlCacheRef.current.delete(attachment.id);
    }

    const docRecord = {
      documentName: sectionName,
      fileName: attachment.fileName,
      size: attachment.fileSize,
      status: (attachment.status || 'PENDING').toLowerCase(),
      lastUpdated: attachment.uploadedAt || new Date().toISOString(),
      comment: attachment.comments || '',
      isSectionLevel,
      sectionId,
      attachmentId: attachment.id,
      fileUrl: attachment.filePath,
    };

    setUploadedDocuments((prev) => [...prev, docRecord]);

    return { attachmentId: attachment.id };
  };

  const handleApproveAttachment = async ({ attachmentId }) => {
    const updated = await reviewInvestmentAttachment(attachmentId, {
      status: 'APPROVED',
      comment: null,
    });

    setUploadedDocuments((prev) =>
      prev.map((doc) =>
        doc.attachmentId === attachmentId
          ? {
              ...doc,
              status: (updated.status || 'APPROVED').toLowerCase(),
              lastUpdated: updated.reviewedAt || doc.lastUpdated,
              comment: updated.comments || doc.comment,
            }
          : doc
      )
    );
    
    // Notify parent component to refresh data (for list/grid view updates)
    if (typeof onSave === 'function') {
      onSave({ 
        type: 'attachment_approved',
        attachmentId,
        declarationData 
      });
    }
  };

  const handleRejectAttachment = async ({ attachmentId, reason }) => {
    const updated = await reviewInvestmentAttachment(attachmentId, {
      status: 'REJECTED',
      comment: reason,
    });

    setUploadedDocuments((prev) =>
      prev.map((doc) =>
        doc.attachmentId === attachmentId
          ? {
              ...doc,
              status: (updated.status || 'REJECTED').toLowerCase(),
              lastUpdated: updated.reviewedAt || doc.lastUpdated,
              comment: updated.comments || doc.comment,
            }
          : doc
      )
    );
    
    // Notify parent component to refresh data (for list/grid view updates)
    if (typeof onSave === 'function') {
      onSave({ 
        type: 'attachment_rejected',
        attachmentId,
        declarationData 
      });
    }
  };

  const handleRemoveAttachment = async (attachmentId) => {
    await deleteInvestmentAttachment(attachmentId);
    setUploadedDocuments((prev) => prev.filter((doc) => doc.attachmentId !== attachmentId));
  };

  const handleBulkAction = (sectionName, action, reason = '') => {
    // TODO: optionally wire bulk approve/reject via reviewInvestmentAttachment for all matching docs
  };

  // Backwards-compatibility wrappers so existing JSX still compiles while routing
  // everything through the new admin upload handler.
  const handleProofUpload = (payload) => handleAdminDocumentUpload(payload);
  const handleDocumentUpload = (payload, sectionId) =>
    handleAdminDocumentUpload(payload, sectionId);

  

  // --------------------- SECTION NAVIGATION HELPERS ---------------------
  const getCurrentSection = () => {
    return tabs[currentSectionIndex];
  };

  const goToNextSection = () => {
    if (currentSectionIndex < tabs.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  };

  const goToPreviousSection = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    }
  };

  const goToSection = (sectionId) => {
    const index = tabs.findIndex(tab => tab.id === sectionId);
    if (index !== -1) {
      setCurrentSectionIndex(index);
    }
  };

  // --------------------- SECTION SAVE HELPERS ---------------------
  const saveCurrentSection = async () => {
    const currentSection = getCurrentSection();
    const sectionId = currentSection.id;

    if (!validateCurrentSection(sectionId)) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    
    try {
      // Get the declaration ID first
      const effectiveDeclarationId = await ensureDeclarationId();
      
      // Prepare the data for the current section
      let sectionData = {};

      // Header fields for previous employment
      // Only convert to numbers if values are present (validation ensures required fields are filled)
      const headerPart = {
        ...buildPrevEmploymentHeaderPart(),
        // Use original override state (not current checkbox state) when saving form sections
        allowAllEdits: originalOverrideState.allowAllEdits,
        allowPrevEmploymentEdit: originalOverrideState.allowPrevEmploymentEdit,
        allowHraEdit: originalOverrideState.allowHraEdit,
        allowSection80CEdit: originalOverrideState.allowSection80CEdit,
        allowViaEdit: originalOverrideState.allowViaEdit,
        allowHousePropertyEdit: originalOverrideState.allowHousePropertyEdit,
      };
      
      // Map section data based on section ID
      switch (sectionId) {
        case 'prevEmployment':
          // Previous employment is stored in declaration header fields
          sectionData = {};
          break;
        case 'hra':
          sectionData = {
            hra: declarationData.hraEntries.map(entry => ({
              isMetro: entry.cityType === 'metro',
              totalRentPaid: parseFloat(entry.totalRentPaid) || 0,
              landlordPan: entry.landlordPan,
              rentalAddress: entry.rentalAddress
            }))
          };
          break;
        case 'section80C':
          sectionData = {
            section80c: {
              lifeInsurancePremium: parseFloat(declarationData.section80C.lifeInsurance) || 0,
              ppf: parseFloat(declarationData.section80C.ppf) || 0,
              vpf: parseFloat(declarationData.section80C.vpf) || 0,
              pensionFund: parseFloat(declarationData.section80C.pensionFund) || 0,
              nsc: parseFloat(declarationData.section80C.nsc) || 0,
              nscInterestReinvested: parseFloat(declarationData.section80C.nscInterest) || 0,
              ulip: parseFloat(declarationData.section80C.ulip) || 0,
              elss: parseFloat(declarationData.section80C.elss) || 0,
              tuitionFees: parseFloat(declarationData.section80C.tuitionFees) || 0,
              principalHousingLoan: parseFloat(declarationData.section80C.houseLoanPrincipal) || 0,
              registrationCharges: parseFloat(declarationData.section80C.registrationCharges) || 0,
              sukanyaSamriddhi: parseFloat(declarationData.section80C.sukanyaSamriddhi) || 0,
              infrastructureBonds: parseFloat(declarationData.section80C.infrastructureBonds) || 0,
              bankFixedDeposit: parseFloat(declarationData.section80C.fixedDeposit) || 0,
            }
          };
          break;
        case 'viaDeductions': {
          const via = declarationData.sectionVIA || {};
          const d = via.section80D || {};
          const o = via.otherInvestments || {};
          sectionData = {
            via: {
              selfMedicalInsurance: parseFloat(d.self) || 0,
              parentsMedicalInsurance: parseFloat(d.parents) || 0,
              seniorCitizenParentsMedical: parseFloat(d.parentsSenior) || 0,
              preventiveHealthCheckup: parseFloat(d.preventiveHealthCheckup) || 0,
              interestEducationLoan: parseFloat(o.section80E) || 0,
              npsAdditionalContribution: parseFloat(o.section80CCD1B) || 0,
              interestOnHomeLoan80EE: parseFloat(o.section80EE) || 0,
              interestOnHomeLoan80EEA: parseFloat(o.section80EEA) || 0,
              interestOnHomeLoan80EEB: parseFloat(o.section80EEB) || 0,
              donations80G: parseFloat(o.section80G) || 0,
              interestOnSavings80TTA: parseFloat(o.section80TTA) || 0,
              interestOnDeposits80TTB: parseFloat(o.section80TTB) || 0,
            }
          };
          break;
        }
        case 'houseProperty':
          sectionData = {
            houseProperty: (declarationData.housePropertyEntries || []).map(entry => ({
              // Map UI values (self-occupied / let-out) to backend ENUMs (SELF_OCCUPIED / LET_OUT)
              occupancy:
                entry.propertyType === 'let-out'
                  ? 'LET_OUT'
                  : 'SELF_OCCUPIED',
              loanBeforeApr1999: !!entry.loanTakenBeforeApril1999,
              interestPaidOnBorrowedCapital: parseFloat(entry.interest) || 0,
              propertyAddress: entry.address || '',
            }))
          };
          break;
        default:
          console.warn('Unknown section ID:', sectionId);
          return;
      }
      
      // Make the API call to save the section data using shared API helper
      const payload = {
        financialYear: getEffectiveFinancialYear(),
        taxRegime: selectedTaxRegime === 'old' ? 'OLD' : 'NEW',
        ...headerPart,
        ...sectionData,
      };

      const result = await payrollService.saveInvestmentData(employee.id, payload);

      if (!result?.success) {
        toast.error(result?.message || `Failed to save ${currentSection.label} details`);
        throw new Error('Failed to save section data');
      }
      
      // Mark section as saved
      setSavedSections(prev => new Set([...prev, sectionId]));
      setErrors({});
      toast.success(`${currentSection.label} details saved successfully!`);

      // After save, treat current regime as "initial" so Cancel won't show popup
      initialSelectedTaxRegimeRef.current = selectedTaxRegime;

      // Notify parent component to refresh data (for list/grid view updates)
      if (typeof onSave === 'function') {
        onSave({ 
          type: 'section_saved',
          sectionId,
          taxRegime: selectedTaxRegime,
          declarationData 
        });
      }
      
      // Auto-navigate to next section
      setTimeout(() => {
        goToNextSection();
      }, 1000);
      
    } catch (error) {
      console.error('Error saving section:', error);
      toast.error(`Failed to save ${currentSection.label} details: ${error.message}`);
    }
  };

  // Save only Employee Access Overrides
  const handleSaveEmployeeAccessOverrides = async () => {
    try {
      // Only send the 6 override flags - don't send taxRegime, status, or any other fields
      // This ensures we only update the override toggles and nothing else
      const payload = {
        financialYear: getEffectiveFinancialYear(),
        allowAllEdits: allowAllEdits,
        allowPrevEmploymentEdit: allowPrevEmploymentEdit,
        allowHraEdit: allowHraEdit,
        allowSection80CEdit: allowSection80CEdit,
        allowViaEdit: allowViaEdit,
        allowHousePropertyEdit: allowHousePropertyEdit,
      };

      const result = await payrollService.saveInvestmentData(employee.id, payload);

      if (!result?.success) {
        toast.error(result?.message || 'Failed to save Employee Access Overrides');
        return;
      }

      // Update the original override state so that future form saves use these values
      setOriginalOverrideState({
        allowAllEdits,
        allowPrevEmploymentEdit,
        allowHraEdit,
        allowSection80CEdit,
        allowViaEdit,
        allowHousePropertyEdit,
      });

      toast.success('Employee Access Overrides saved successfully!');
      
      // Notify parent component to refresh data (for list/grid view updates)
      if (typeof onSave === 'function') {
        onSave({ 
          type: 'overrides_saved',
          declarationData 
        });
      }
    } catch (error) {
      console.error('Error saving Employee Access Overrides:', error);
      toast.error(`Failed to save Employee Access Overrides: ${error.message}`);
    }
  };

  const resetCurrentSection = () => {
    const currentSection = getCurrentSection();
    const sectionId = currentSection.id;
    
    // Reset section data based on section type
    switch (sectionId) {
      case 'hra':
        setDeclarationData(prev => ({
          ...prev,
          hraEntries: [
            {
              cityType: 'metro',
              totalRentPaid: '',
              landlordPan: '',
              rentalAddress: '',
            },
          ],
        }));
        break;
      case 'section80C':
        setDeclarationData(prev => ({
          ...prev,
          section80C: {
            lifeInsurance: '',
            ppf: '',
            vpf: '',
            pensionFund: '',
            nsc: '',
            nscInterest: '',
            ulip: '',
            elss: '',
            tuitionFees: '',
            houseLoanPrincipal: '',
            registrationCharges: '',
            sukanyaSamriddhi: '',
            infrastructureBonds: '',
            fixedDeposit: '',
          }
        }));
        break;
      case 'viaDeductions':
        setDeclarationData(prev => ({
          ...prev,
          sectionVIA: {
            section80D: {
              self: '',
              parents: '',
              parentsSenior: '',
              isSelfSeniorCitizen: false,
              isParentsSeniorCitizen: false,
              preventiveHealthCheckup: '',
            },
            otherInvestments: {
              section80E: '',
              section80CCD1B: '',
              section80EE: '',
              section80EEA: '',
              section80EEB: '',
              section80G: '',
              section80TTA: '',
              section80TTB: '',
            },
          }
        }));
        break;
      case 'houseProperty':
        setDeclarationData(prev => ({
          ...prev,
          housePropertyEntries: [
            {
              propertyType: 'self-occupied',
              loanTakenBeforeApril1999: false,
              interest: '',
              rentalIncome: '',
              address: '',
            },
          ],
        }));
        break;
    }
  };

  const submitDeclaration = () => {
    // Open confirmation modal based on selected tax regime
    if (selectedTaxRegime === 'old') {
      setSubmitConfirm({ open: true, mode: 'old' });
    } else if (selectedTaxRegime === 'new') {
      setSubmitConfirm({ open: true, mode: 'new' });
    }
  };

  const handleConfirmSubmit = async () => {
    try {
      const mode = submitConfirm.mode;
      if (!mode) {
        setSubmitConfirm({ open: false, mode: null });
        return;
      }

      // Build header payload including previous employment values
      const headerPart = {
        ...buildPrevEmploymentHeaderPart(),
        allowAllEdits,
        allowPrevEmploymentEdit,
        allowHraEdit,
        allowSection80CEdit,
        allowViaEdit,
        allowHousePropertyEdit,
      };

      const financialYear = getEffectiveFinancialYear();

      if (mode === 'old') {
        const payload = {
          financialYear,
          taxRegime: 'OLD',
          status: 'SUBMITTED',
          ...headerPart,
        };

        const result = await payrollService.saveInvestmentData(employee.id, payload);
        if (!result?.success) {
          console.error('Failed to submit declaration (old regime):', result);
          toast.error(result?.message || 'Failed to submit declaration');
          return;
        }

        // Mark all sections as saved for UI
        const allSections = ['hra', 'section80C', 'viaDeductions', 'houseProperty'];
        setSavedSections(new Set(allSections));

        initialSelectedTaxRegimeRef.current = 'old';
        toast.success('Declaration submitted successfully under Old Tax Regime');
        onSave(declarationData);
        setShowSubmitSuccessModal(true);
      } else if (mode === 'new') {
        // New regime submission flow
        try {
          await payrollService.deleteInvestmentData(employee.id, financialYear);
        } catch (err) {
          console.error(
            'Failed to clear old regime investment data before submitting new regime (admin):',
            err
          );

          // If there is simply no existing declaration to delete (404), continue.
          const status = err?.response?.status;
          if (status && status !== 404) {
            toast.error('Failed to clear existing investment data. Please try again.');
            return;
          }
        }

        const payload = {
          financialYear,
          taxRegime: 'NEW',
          status: 'SUBMITTED',
          ...headerPart,
        };

        const result = await payrollService.saveInvestmentData(employee.id, payload);
        if (!result?.success) {
          console.error('Failed to submit declaration (new regime):', result);
          toast.error(result?.message || 'Failed to submit declaration');
          return;
        }

        initialSelectedTaxRegimeRef.current = 'new';
        toast.success('Declaration submitted successfully under New Tax Regime');
        onSave(declarationData);
        setShowSubmitSuccessModal(true);
      }
    } catch (error) {
      console.error('Error submitting declaration (admin):', error);
      toast.error(error?.message || 'Error submitting declaration');
    } finally {
      setSubmitConfirm({ open: false, mode: null });
    }
  };

  const handleSubmitSuccessClose = () => {
    setShowSubmitSuccessModal(false);
    onClose();
  };

  // Example "renderProofDocuments" similar to your first snippet
  const renderProofDocuments = () => (
    <div className="space-y-4 mt-6">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg">Proof Documents</h3>
        {!isReadOnly && (
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="text-green-500 flex items-center hover:text-green-600 text-sm"
          >
            <FileUp className="mr-1" size={16} />
            Drop Files Here
          </button>
        )}
      </div>

      <div
        className="border overflow-hidden"
        style={{ maxHeight: '300px', overflowY: 'auto' }}
      >
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Sr. No.</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Document Name</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">File Name</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Last Updated</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Comments</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {uploadedDocuments.map((doc, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm">{index + 1}</td>
                <td className="px-4 py-2 text-sm">{doc.documentName}</td>
                <td className="px-4 py-2 text-sm">{doc.fileName}</td>
                <td className="px-4 py-2 text-sm">
                  {format(new Date(doc.lastUpdated), 'dd-MMM-yyyy')}
                </td>
                <td className="px-4 py-2 text-sm">{doc.comment}</td>
                <td className="px-4 py-2 text-sm">
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                    Uploaded
                  </span>
                </td>
              </tr>
            ))}
            {uploadedDocuments.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                  No documents uploaded yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
  // -------------------------------------------------------------

  // --------------------- HANDLERS ---------------------
  const handleInputChange = (section, subsection, field, value) => {
    setDeclarationData((prev) => ({
      ...prev,
      [section]: subsection
        ? {
            ...prev[section],
            [subsection]: {
              ...prev[section][subsection],
              [field]: value,
            },
          }
        : {
            ...prev[section],
            [field]: value,
          },
    }));

    // Inline validation
    if (section === 'section80C') {
      const labelMap = {
        lifeInsurance: 'Life Insurance',
        ppf: 'PPF',
        vpf: 'VPF',
        pensionFund: 'Pension Fund',
        nsc: 'NSC',
        nscInterest: 'NSC Interest',
        ulip: 'ULIP',
        elss: 'ELSS',
        tuitionFees: 'Tuition Fees',
        houseLoanPrincipal: 'House Loan Principal',
        registrationCharges: 'Registration Charges',
        sukanyaSamriddhi: 'Sukanya Samriddhi',
        infrastructureBonds: 'Infrastructure Bonds',
        fixedDeposit: 'Fixed Deposit',
      };
      if (labelMap[field]) {
        setFieldError(field, getNumberRangeError(labelMap[field], value));
      }
    }

    if (section === 'sectionVIA' && subsection === 'section80D') {
      const labelMap = {
        self: 'Self Medical Insurance',
        parents: 'Parents Medical Insurance',
        preventiveHealthCheckup: 'Preventive Health Checkup',
        parentsSenior: 'Senior Citizen Parents Medical Insurance',
      };
      const idMap = {
        self: 'section80DSelf',
        parents: 'section80DParents',
        preventiveHealthCheckup: 'preventiveHealthCheckup',
        parentsSenior: 'section80DParentsSenior',
      };
      if (labelMap[field]) {
        setFieldError(idMap[field], getNumberRangeError(labelMap[field], value));
      }
    }

    if (section === 'sectionVIA' && subsection === 'otherInvestments') {
      const labelMap = {
        section80E: 'Interest Education Loan',
        section80CCD1B: 'NPS Additional Contribution',
        section80EE: 'Interest on Home Loan 80EE',
        section80G: 'Donations 80G',
        section80TTA: 'Interest on Savings 80TTA',
        section80TTB: 'Interest on Deposits 80TTB',
      };
      if (labelMap[field]) {
        setFieldError(field, getNumberRangeError(labelMap[field], value));
      }
    }
  };

  // --- HRA (multiple entries) helpers ---

  const addHraEntry = () => {
    setDeclarationData((prev) => ({
      ...prev,
      hraEntries: [
        ...((Array.isArray(prev.hraEntries) ? prev.hraEntries : [])),
        {
          cityType: 'metro',
          totalRentPaid: '',
          landlordPan: '',
          rentalAddress: '',
        },
      ],
    }));
  };

  const removeHraEntry = (index) => {
    setDeclarationData((prev) => {
      const current = Array.isArray(prev.hraEntries) ? prev.hraEntries : [];
      const next = current.filter((_, i) => i !== index);
      return {
        ...prev,
        hraEntries: next.length
          ? next
          : [
              {
                cityType: 'metro',
                totalRentPaid: '',
                landlordPan: '',
                rentalAddress: '',
              },
            ],
      };
    });
  };

  const handleHraChange = (index, field, value) => {
    // Normalize PAN to uppercase and limit to 10 characters
    let nextValue = value;
    if (field === 'landlordPan' && typeof value === 'string') {
      nextValue = value.toUpperCase().slice(0, 10); // Limit to 10 characters
    }

    setDeclarationData((prev) => {
      const updatedEntries = (Array.isArray(prev.hraEntries) ? prev.hraEntries : []).map((item, i) =>
        i === index ? { ...item, [field]: nextValue } : item
      );
      
      // Validate after state update using the updated entry
      const updatedRow = updatedEntries[index];
      
      if (field === 'totalRentPaid') {
        const amount = Number(value) || 0;
        if (amount > 100000) {
          const landlordPan = (updatedRow?.landlordPan || '').trim();
          if (!landlordPan) {
            setFieldError(
              `hra-landlordPan-${index}`,
              `Landlord PAN is mandatory when Total Rent Paid is more than ₹1,00,000`
            );
          } else {
            // Still validate PAN format if provided
            const panValidationErr = getPanError(`Landlord PAN`, landlordPan);
            if (panValidationErr) {
              setFieldError(`hra-landlordPan-${index}`, panValidationErr);
            }
          }
        }
      }
      
      if (field === 'landlordPan') {
        const amount = Number(updatedRow?.totalRentPaid) || 0;
        if (amount > 100000 && !nextValue.trim()) {
          setFieldError(
            `hra-landlordPan-${index}`,
            `Landlord PAN is mandatory when Total Rent Paid is more than ₹1,00,000`
          );
        }
      }
      
      return {
        ...prev,
        hraEntries: updatedEntries,
      };
    });

    if (field === 'totalRentPaid') {
      setFieldError(
        `hra-rentPaid-${index}`,
        getNumberRangeError(`Total Rent Paid `, value)
      );
    }
    if (field === 'landlordPan') {
      setFieldError(
        `hra-landlordPan-${index}`,
        getPanError(`Landlord PAN`, nextValue)
      );
    }
    if (field === 'rentalAddress') {
      setFieldError(
        `hra-address-${index}`,
        getAddressError(`Rental Address `, value)
      );
    }
  };

  // --- House Property (multiple entries) helpers ---

  const addHousePropertyEntry = () => {
    setDeclarationData((prev) => ({
      ...prev,
      housePropertyEntries: [
        ...((Array.isArray(prev.housePropertyEntries) ? prev.housePropertyEntries : [])),
        {
          propertyType: 'self-occupied',
          loanTakenBeforeApril1999: false,
          interest: '',
          rentalIncome: '',
          address: '',
        },
      ],
    }));
  };

  const removeHousePropertyEntry = (index) => {
    setDeclarationData((prev) => {
      const current = Array.isArray(prev.housePropertyEntries) ? prev.housePropertyEntries : [];
      const next = current.filter((_, i) => i !== index);
      return {
        ...prev,
        housePropertyEntries: next.length
          ? next
          : [
              {
                propertyType: 'self-occupied',
                loanTakenBeforeApril1999: false,
                interest: '',
                rentalIncome: '',
                address: '',
              },
            ],
      };
    });
  };

  const handleHousePropertyChange = (index, field, value) => {
    setDeclarationData((prev) => ({
      ...prev,
      housePropertyEntries: (Array.isArray(prev.housePropertyEntries) ? prev.housePropertyEntries : []).map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));

    if (field === 'interest') {
      setFieldError(
        `interest-${index}`,
        getNumberRangeError(`Interest Paid on Borrowed Capital `, value)
      );
    }
  };

  // --------------------- SECTION TOTAL HELPERS ---------------------

  const getSection80CTotal = () => {
    const s = declarationData.section80C || {};
    const keys = [
      'lifeInsurance',
      'ppf',
      'vpf',
      'pensionFund',
      'nsc',
      'nscInterest',
      'ulip',
      'elss',
      'tuitionFees',
      'houseLoanPrincipal',
      'registrationCharges',
      'sukanyaSamriddhi',
      'infrastructureBonds',
      'fixedDeposit',
    ];
    return keys.reduce((sum, key) => {
      const raw = s[key];
      const num = raw === '' || raw == null ? 0 : Number(raw) || 0;
      return sum + num;
    }, 0);
  };

  const getViaDeductionsTotal = () => {
    const via = declarationData.sectionVIA || {};
    const d = via.section80D || {};
    const o = via.otherInvestments || {};

    const values = [
      d.self,
      d.parents,
      d.parentsSenior,
      d.preventiveHealthCheckup,
      o.section80E,
      o.section80CCD1B,
      o.section80EE,
      o.section80EEA,
      o.section80EEB,
      o.section80G,
      o.section80TTA,
      o.section80TTB,
    ];

    return values.reduce((sum, raw) => {
      const num = raw === '' || raw == null ? 0 : Number(raw) || 0;
      return sum + num;
    }, 0);
  };

  const hasAnyDeclaration = () => {
    const s80c = getSection80CTotal();
    const via = getViaDeductionsTotal();
    if (s80c > 0 || via > 0) return true;
    const hasHraData = (declarationData.hraEntries || []).some(
      (e) => (e.totalRentPaid != null && e.totalRentPaid !== '' && Number(e.totalRentPaid)) > 0
    );
    if (hasHraData) return true;
    const hasHpData = (declarationData.housePropertyEntries || []).some(
      (e) => (e.interest != null && e.interest !== '' && Number(e.interest)) > 0
    );
    return hasHpData;
  };

  const handleCancelOrClose = () => {
    // Old cancel popup: no old declaration data (other than prev employment) and currently in old regime
    if (selectedTaxRegime === 'old' && !hasAnyDeclaration()) {
      setCancelConfirmMode('old_nothing_declared');
      return;
    }
    // New cancel popup: there is old regime data and currently in new regime
    if (selectedTaxRegime === 'new' && hasAnyDeclaration()) {
      setCancelConfirmMode('new');
      return;
    }
    onClose();
  };

  // --------------------- RENDER TABS ---------------------
  // 1. Section 80C
  const isReadOnly = mode === 'view' || isInvestmentDeclarationReadOnly();
  // Proof uploads should only be available when declaration itself is editable
  // and when the proof window/settings allow uploads.
  const proofUploadEnabled = !isReadOnly && !isProofUploadReadOnly();
  const taxRegimeChangeAllowed = !isTaxRegimeLocked();
  
  const renderSection80C = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Section 80C Deductions</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
        <FloatingInput
          id="lifeInsurance"
          label="Life Insurance Premium"
          type="number"
          value={declarationData.section80C.lifeInsurance}
          onChange={(e) => handleInputChange('section80C', null, 'lifeInsurance', e.target.value)}
          disabled={isReadOnly}
          error={errors.lifeInsurance}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Life Insurance Premium" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Life Insurance Premium')?.attachmentId}
              initialFileName={getFieldAttachment('Life Insurance Premium')?.fileName}
              initialFileSize={getFieldAttachment('Life Insurance Premium')?.size}
              initialStatus={getFieldAttachment('Life Insurance Premium')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Life Insurance Premium')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="ppf"
          label="Public Provident Fund (PPF)"
          type="number"
          value={declarationData.section80C.ppf}
          onChange={(e) => handleInputChange('section80C', null, 'ppf', e.target.value)}
          disabled={isReadOnly}
          error={errors.ppf}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Public Provident Fund (PPF)" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Public Provident Fund (PPF)')?.attachmentId}
              initialFileName={getFieldAttachment('Public Provident Fund (PPF)')?.fileName}
              initialFileSize={getFieldAttachment('Public Provident Fund (PPF)')?.size}
              initialStatus={getFieldAttachment('Public Provident Fund (PPF)')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Public Provident Fund (PPF)')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="vpf"
          label="Voluntary Provident Fund (VPF)"
          type="number"
          value={declarationData.section80C.vpf}
          onChange={(e) => handleInputChange('section80C', null, 'vpf', e.target.value)}
          disabled={isReadOnly}
          error={errors.vpf}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Voluntary Provident Fund (VPF)" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Voluntary Provident Fund (VPF)')?.attachmentId}
              initialFileName={getFieldAttachment('Voluntary Provident Fund (VPF)')?.fileName}
              initialFileSize={getFieldAttachment('Voluntary Provident Fund (VPF)')?.size}
              initialStatus={getFieldAttachment('Voluntary Provident Fund (VPF)')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Voluntary Provident Fund (VPF)')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="pensionFund"
          label="Pension Fund Contribution"
          type="number"
          value={declarationData.section80C.pensionFund}
          onChange={(e) => handleInputChange('section80C', null, 'pensionFund', e.target.value)}
          disabled={isReadOnly}
          error={errors.pensionFund}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Pension Fund Contribution" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Pension Fund Contribution')?.attachmentId}
              initialFileName={getFieldAttachment('Pension Fund Contribution')?.fileName}
              initialFileSize={getFieldAttachment('Pension Fund Contribution')?.size}
              initialStatus={getFieldAttachment('Pension Fund Contribution')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Pension Fund Contribution')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="nsc"
          label="National Savings Certificate (NSC)"
          type="number"
          value={declarationData.section80C.nsc}
          onChange={(e) => handleInputChange('section80C', null, 'nsc', e.target.value)}
          disabled={isReadOnly}
          error={errors.nsc}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="National Savings Certificate (NSC)" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('National Savings Certificate (NSC)')?.attachmentId}
              initialFileName={getFieldAttachment('National Savings Certificate (NSC)')?.fileName}
              initialFileSize={getFieldAttachment('National Savings Certificate (NSC)')?.size}
              initialStatus={getFieldAttachment('National Savings Certificate (NSC)')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('National Savings Certificate (NSC)')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="nscInterest"
          label="Interest Accrued on NSC (Reinvested)"
          type="number"
          value={declarationData.section80C.nscInterest}
          onChange={(e) => handleInputChange('section80C', null, 'nscInterest', e.target.value)}
          disabled={isReadOnly}
          error={errors.nscInterest}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Interest Accrued on NSC (Reinvested)" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Interest Accrued on NSC (Reinvested)')?.attachmentId}
              initialFileName={getFieldAttachment('Interest Accrued on NSC (Reinvested)')?.fileName}
              initialFileSize={getFieldAttachment('Interest Accrued on NSC (Reinvested)')?.size}
              initialStatus={getFieldAttachment('Interest Accrued on NSC (Reinvested)')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Interest Accrued on NSC (Reinvested)')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="ulip"
          label="Unit Linked Insurance Policy (ULIP)"
          type="number"
          value={declarationData.section80C.ulip}
          onChange={(e) => handleInputChange('section80C', null, 'ulip', e.target.value)}
          disabled={isReadOnly}
          error={errors.ulip}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Unit Linked Insurance Policy (ULIP)" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Unit Linked Insurance Policy (ULIP)')?.attachmentId}
              initialFileName={getFieldAttachment('Unit Linked Insurance Policy (ULIP)')?.fileName}
              initialFileSize={getFieldAttachment('Unit Linked Insurance Policy (ULIP)')?.size}
              initialStatus={getFieldAttachment('Unit Linked Insurance Policy (ULIP)')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Unit Linked Insurance Policy (ULIP)')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="elss"
          label="Equity Linked Savings Schemes (ELSS)"
          type="number"
          value={declarationData.section80C.elss}
          onChange={(e) => handleInputChange('section80C', null, 'elss', e.target.value)}
          disabled={isReadOnly}
          error={errors.elss}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Equity Linked Savings Schemes (ELSS)" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Equity Linked Savings Schemes (ELSS)')?.attachmentId}
              initialFileName={getFieldAttachment('Equity Linked Savings Schemes (ELSS)')?.fileName}
              initialFileSize={getFieldAttachment('Equity Linked Savings Schemes (ELSS)')?.size}
              initialStatus={getFieldAttachment('Equity Linked Savings Schemes (ELSS)')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Equity Linked Savings Schemes (ELSS)')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="tuitionFees"
          label="Payment of Tuition Fees (Max 2 Children)"
          type="number"
          value={declarationData.section80C.tuitionFees}
          onChange={(e) => handleInputChange('section80C', null, 'tuitionFees', e.target.value)}
          disabled={isReadOnly}
          error={errors.tuitionFees}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Payment of Tuition Fees (Max 2 Children)" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Payment of Tuition Fees (Max 2 Children)')?.attachmentId}
              initialFileName={getFieldAttachment('Payment of Tuition Fees (Max 2 Children)')?.fileName}
              initialFileSize={getFieldAttachment('Payment of Tuition Fees (Max 2 Children)')?.size}
              initialStatus={getFieldAttachment('Payment of Tuition Fees (Max 2 Children)')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Payment of Tuition Fees (Max 2 Children)')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="houseLoanPrincipal"
          label="Principal Repayment of Housing Loan"
          type="number"
          value={declarationData.section80C.houseLoanPrincipal}
          onChange={(e) => handleInputChange('section80C', null, 'houseLoanPrincipal', e.target.value)}
          disabled={isReadOnly}
          error={errors.houseLoanPrincipal}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Principal Repayment of Housing Loan" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Principal Repayment of Housing Loan')?.attachmentId}
              initialFileName={getFieldAttachment('Principal Repayment of Housing Loan')?.fileName}
              initialFileSize={getFieldAttachment('Principal Repayment of Housing Loan')?.size}
              initialStatus={getFieldAttachment('Principal Repayment of Housing Loan')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Principal Repayment of Housing Loan')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="registrationCharges"
          label="Registration Charges for Buying House (1 Year Only)"
          type="number"
          value={declarationData.section80C.registrationCharges}
          onChange={(e) => handleInputChange('section80C', null, 'registrationCharges', e.target.value)}
          disabled={isReadOnly}
          error={errors.registrationCharges}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Registration Charges for Buying House" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Registration Charges for Buying House')?.attachmentId}
              initialFileName={getFieldAttachment('Registration Charges for Buying House')?.fileName}
              initialFileSize={getFieldAttachment('Registration Charges for Buying House')?.size}
              initialStatus={getFieldAttachment('Registration Charges for Buying House')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Registration Charges for Buying House')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="sukanyaSamriddhi"
          label="Sukanya Samriddhi Yojana"
          type="number"
          value={declarationData.section80C.sukanyaSamriddhi}
          onChange={(e) => handleInputChange('section80C', null, 'sukanyaSamriddhi', e.target.value)}
          disabled={isReadOnly}
          error={errors.sukanyaSamriddhi}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Sukanya Samriddhi Yojana" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Sukanya Samriddhi Yojana')?.attachmentId}
              initialFileName={getFieldAttachment('Sukanya Samriddhi Yojana')?.fileName}
              initialFileSize={getFieldAttachment('Sukanya Samriddhi Yojana')?.size}
              initialStatus={getFieldAttachment('Sukanya Samriddhi Yojana')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Sukanya Samriddhi Yojana')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="infrastructureBonds"
          label="Infrastructure Bonds"
          type="number"
          value={declarationData.section80C.infrastructureBonds}
          onChange={(e) => handleInputChange('section80C', null, 'infrastructureBonds', e.target.value)}
          disabled={isReadOnly}
          error={errors.infrastructureBonds}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Infrastructure Bonds" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Infrastructure Bonds')?.attachmentId}
              initialFileName={getFieldAttachment('Infrastructure Bonds')?.fileName}
              initialFileSize={getFieldAttachment('Infrastructure Bonds')?.size}
              initialStatus={getFieldAttachment('Infrastructure Bonds')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Infrastructure Bonds')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
        <FloatingInput
          id="fixedDeposit"
          label="Bank Fixed Deposit (5+ Years)"
          type="number"
          value={declarationData.section80C.fixedDeposit}
          onChange={(e) => handleInputChange('section80C', null, 'fixedDeposit', e.target.value)}
          disabled={isReadOnly}
          error={errors.fixedDeposit}
        />
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <SectionUpload 
              sectionName="Bank Fixed Deposit (5+ Years)" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              initialAttachmentId={getFieldAttachment('Bank Fixed Deposit (5+ Years)')?.attachmentId}
              initialFileName={getFieldAttachment('Bank Fixed Deposit (5+ Years)')?.fileName}
              initialFileSize={getFieldAttachment('Bank Fixed Deposit (5+ Years)')?.size}
              initialStatus={getFieldAttachment('Bank Fixed Deposit (5+ Years)')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Bank Fixed Deposit (5+ Years)')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          )}
      </div>
      </div>
      
      {/* Bulk Actions for Section 80C */}
      {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
        <div className="mt-4">
          <BulkActions 
            sectionName="Section 80C" 
            onBulkAction={handleBulkAction} 
            disabled={isReadOnly}
          />
        </div>
      )}
    </div>
  );

  // 2. HRA (House Rent Allowance) - Separate Tab (multiple entries)
  const renderHRA = () => {
    const hraEntries = declarationData.hraEntries || [];

    return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">House Rent Allowance (HRA)</h3>
      
      {hraEntries.map((entry, index) => (
        <div key={index} className="p-4 bg-gray-50 mb-4">
          <div className="space-y-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* City Type Radio Buttons */}
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`hra-cityType-${index}`}
                  value="metro"
                  checked={entry.cityType === 'metro'}
                  onChange={(e) => handleHraChange(index, 'cityType', e.target.value)}
                  disabled={isReadOnly}
                  className="mr-2"
                />
                Metro
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`hra-cityType-${index}`}
                  value="non-metro"
                  checked={entry.cityType === 'non-metro'}
                  onChange={(e) => handleHraChange(index, 'cityType', e.target.value)}
                  disabled={isReadOnly}
                  className="mr-2"
                />
                Non-Metro
              </label>
            </div>

            <FloatingInput
              id={`hra-rentPaid-${index}`}
              label="Total Rent Paid/Payable"
              type="number"
              value={entry.totalRentPaid}
              onChange={(e) => handleHraChange(index, 'totalRentPaid', e.target.value)}
              disabled={isReadOnly}
              error={errors[`hra-rentPaid-${index}`]}
            />
            <FloatingInput
              id={`hra-landlordPan-${index}`}
              label="Landlord PAN"
              value={entry.landlordPan || ''}
              onChange={(e) => {
                const value = e.target.value;
                // Limit to 10 characters (handleHraChange will also enforce this and uppercase)
                if (value.length <= 10) {
                  handleHraChange(index, 'landlordPan', value);
                }
              }}
              disabled={isReadOnly}
              error={errors[`hra-landlordPan-${index}`]}
              maxLength={10}
            />
            <FloatingInput
              id={`hra-address-${index}`}
              label="Rental Address"
              value={entry.rentalAddress}
              onChange={(e) => handleHraChange(index, 'rentalAddress', e.target.value)}
              disabled={isReadOnly}
              error={errors[`hra-address-${index}`]}
            />
          </div>

          {/* Section-level upload for each HRA entry */}
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <div className="mt-4">
              <SectionUpload
                sectionName={`HRA - Entry ${index + 1}`}
                onUpload={handleProofUpload}
                onApprove={handleApproveAttachment}
                onReject={handleRejectAttachment}
                onRemove={handleRemoveAttachment}
                disabled={isReadOnly}
                isSectionLevel={true}
                initialAttachmentId={getFieldAttachment(`HRA - Entry ${index + 1}`)?.attachmentId}
                initialFileName={getFieldAttachment(`HRA - Entry ${index + 1}`)?.fileName}
                initialFileSize={getFieldAttachment(`HRA - Entry ${index + 1}`)?.size}
                initialStatus={getFieldAttachment(`HRA - Entry ${index + 1}`)?.status || 'pending'}
                initialRejectReason={getFieldAttachment(`HRA - Entry ${index + 1}`)?.comment || ''}
                onViewAttachment={openAttachment}
              />
            </div>
          )}

          {!isReadOnly && index > 0 && (
            <button
              type="button"
              className="mt-2 text-red-600 hover:text-red-800 text-sm"
              onClick={() => removeHraEntry(index)}
            >
              Remove HRA
            </button>
          )}
        </div>
      ))}

      {/* {!isReadOnly && (
        <button
          type="button"
          onClick={addHraEntry}
          className="m-2 text-redhalo-navy hover:text-redhalo-navy-dark text-sm"
        >
          + Add HRA
        </button>
      )} */}
    </div>
  );
  };

  // 3. Section 80D - Medical Insurance Only
  const renderSection80D = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Section 80D - Medical Insurance</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* 80D */}
        <FloatingInput
          id="section80DSelf"
          label="Self Medical Insurance (80D)"
          type="number"
          value={declarationData.sectionVIA.section80D.self}
          onChange={(e) => handleInputChange('sectionVIA', 'section80D', 'self', e.target.value)}
          disabled={isReadOnly}
          error={errors.section80DSelf}
        />
        <FloatingInput
          id="section80DParents"
          label="Parents Medical Insurance (80D)"
          type="number"
          value={declarationData.sectionVIA.section80D.parents}
          onChange={(e) => handleInputChange('sectionVIA', 'section80D', 'parents', e.target.value)}
          disabled={isReadOnly}
          error={errors.section80DParents}
        />

        {/* Senior Citizen Checkboxes */}
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="selfSeniorCitizen"
            checked={declarationData.sectionVIA.section80D.isSelfSeniorCitizen}
            onChange={(e) =>
              handleInputChange('sectionVIA', 'section80D', 'isSelfSeniorCitizen', e.target.checked)
            }
            className="mr-2"
            disabled={isReadOnly}
          />
          <label htmlFor="selfSeniorCitizen">Self Senior Citizen</label>
        </div>
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="parentsSeniorCitizen"
            checked={declarationData.sectionVIA.section80D.isParentsSeniorCitizen}
            onChange={(e) =>
              handleInputChange(
                'sectionVIA',
                'section80D',
                'isParentsSeniorCitizen',
                e.target.checked,
              )
            }
            className="mr-2"
            disabled={isReadOnly}
          />
          <label htmlFor="parentsSeniorCitizen">Parents Senior Citizen</label>
        </div>

        <FloatingInput
          id="preventiveHealthCheckup"
          label="Preventive Health Checkup (80D)"
          type="number"
          value={declarationData.sectionVIA.section80D.preventiveHealthCheckup}
          onChange={(e) =>
            handleInputChange('sectionVIA', 'section80D', 'preventiveHealthCheckup', e.target.value)
          }
          disabled={isReadOnly}
          error={errors.preventiveHealthCheckup}
        />
      </div>
    </div>
  );

  // 4. VIA-deductions (Combined Section 80D and Other Investments)
  const renderVIADeductions = () => (
    <div className="space-y-6">
      {/* Section 80D - Medical Insurance */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold mb-4">Section 80D - Medical Insurance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FloatingInput
            id="section80DSelf"
            label="Self Medical Insurance (80D)"
            type="number"
            value={declarationData.sectionVIA.section80D.self}
            onChange={(e) => handleInputChange('sectionVIA', 'section80D', 'self', e.target.value)}
            disabled={isReadOnly}
            error={errors.section80DSelf}
          />
          <FloatingInput
            id="section80DParents"
            label="Parents Medical Insurance (80D)"
            type="number"
            value={declarationData.sectionVIA.section80D.parents}
            onChange={(e) => handleInputChange('sectionVIA', 'section80D', 'parents', e.target.value)}
            disabled={isReadOnly}
            error={errors.section80DParents}
          />
          <FloatingInput
            id="section80DParentsSenior"
            label="Senior Citizen Parents Medical Insurance (80D)"
            type="number"
            value={declarationData.sectionVIA.section80D.parentsSenior}
            onChange={(e) => handleInputChange('sectionVIA', 'section80D', 'parentsSenior', e.target.value)}
            disabled={isReadOnly}
            error={errors.section80DParentsSenior}
          />
          <FloatingInput
            id="preventiveHealthCheckup"
            label="Preventive Health Checkup (80D)"
            type="number"
            value={declarationData.sectionVIA.section80D.preventiveHealthCheckup}
            onChange={(e) =>
              handleInputChange('sectionVIA', 'section80D', 'preventiveHealthCheckup', e.target.value)
            }
            disabled={isReadOnly}
            error={errors.preventiveHealthCheckup}
          />
        </div>
        
        {/* Section-level upload for Section 80D */}
        {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
          <div className="mt-4">
            <SectionUpload 
              sectionName="Section 80D - Medical Insurance" 
              onUpload={handleProofUpload}
              onApprove={handleApproveAttachment}
              onReject={handleRejectAttachment}
              onRemove={handleRemoveAttachment}
              disabled={isReadOnly}
              isSectionLevel={true}
              initialAttachmentId={getFieldAttachment('Section 80D - Medical Insurance')?.attachmentId}
              initialFileName={getFieldAttachment('Section 80D - Medical Insurance')?.fileName}
              initialFileSize={getFieldAttachment('Section 80D - Medical Insurance')?.size}
              initialStatus={getFieldAttachment('Section 80D - Medical Insurance')?.status || 'pending'}
              initialRejectReason={getFieldAttachment('Section 80D - Medical Insurance')?.comment || ''}
              onViewAttachment={openAttachment}
            />
          </div>
        )}
    </div>

      {/* Other Investments */}
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Other Investments</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
        <FloatingInput
          id="section80E"
          label="Interest on Education Loan (80E)"
          type="number"
          value={declarationData.sectionVIA.otherInvestments.section80E}
          onChange={(e) => handleInputChange('sectionVIA', 'otherInvestments', 'section80E', e.target.value)}
          disabled={isReadOnly}
          error={errors.section80E}
        />
            {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
              <SectionUpload 
                sectionName="Interest on Education Loan (80E)" 
                onUpload={handleProofUpload}
                onApprove={handleApproveAttachment}
                onReject={handleRejectAttachment}
                onRemove={handleRemoveAttachment}
                disabled={isReadOnly}
                initialAttachmentId={getFieldAttachment('Interest on Education Loan (80E)')?.attachmentId}
                initialFileName={getFieldAttachment('Interest on Education Loan (80E)')?.fileName}
                initialFileSize={getFieldAttachment('Interest on Education Loan (80E)')?.size}
                initialStatus={getFieldAttachment('Interest on Education Loan (80E)')?.status || 'pending'}
                initialRejectReason={getFieldAttachment('Interest on Education Loan (80E)')?.comment || ''}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
        <FloatingInput
          id="section80CCD1B"
          label="NPS Additional Contribution (80CCD(1B))"
          type="number"
          value={declarationData.sectionVIA.otherInvestments.section80CCD1B}
          onChange={(e) => handleInputChange('sectionVIA', 'otherInvestments', 'section80CCD1B', e.target.value)}
          disabled={isReadOnly}
          error={errors.section80CCD1B}
        />
            {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
              <SectionUpload 
                sectionName="NPS Additional Contribution (80CCD(1B))" 
                onUpload={handleProofUpload}
                onApprove={handleApproveAttachment}
                onReject={handleRejectAttachment}
                onRemove={handleRemoveAttachment}
                disabled={isReadOnly}
                initialAttachmentId={getFieldAttachment('NPS Additional Contribution (80CCD(1B))')?.attachmentId}
                initialFileName={getFieldAttachment('NPS Additional Contribution (80CCD(1B))')?.fileName}
                initialFileSize={getFieldAttachment('NPS Additional Contribution (80CCD(1B))')?.size}
                initialStatus={getFieldAttachment('NPS Additional Contribution (80CCD(1B))')?.status || 'pending'}
                initialRejectReason={getFieldAttachment('NPS Additional Contribution (80CCD(1B))')?.comment || ''}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
        <FloatingInput
          id="section80EE"
          label="Interest on Home Loan (80EE)"
          type="number"
          value={declarationData.sectionVIA.otherInvestments.section80EE}
          onChange={(e) => handleInputChange('sectionVIA', 'otherInvestments', 'section80EE', e.target.value)}
          disabled={isReadOnly}
          error={errors.section80EE}
        />
            {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
              <SectionUpload 
                sectionName="Interest on Home Loan (80EE)" 
                onUpload={handleProofUpload}
                onApprove={handleApproveAttachment}
                onReject={handleRejectAttachment}
                onRemove={handleRemoveAttachment}
                disabled={isReadOnly}
                initialAttachmentId={getFieldAttachment('Interest on Home Loan (80EE)')?.attachmentId}
                initialFileName={getFieldAttachment('Interest on Home Loan (80EE)')?.fileName}
                initialFileSize={getFieldAttachment('Interest on Home Loan (80EE)')?.size}
                initialStatus={getFieldAttachment('Interest on Home Loan (80EE)')?.status || 'pending'}
                initialRejectReason={getFieldAttachment('Interest on Home Loan (80EE)')?.comment || ''}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
        <FloatingInput
          id="section80EEA"
          label="Interest on Home Loan (80EEA)"
          type="number"
          value={declarationData.sectionVIA.otherInvestments.section80EEA}
          onChange={(e) => handleInputChange('sectionVIA', 'otherInvestments', 'section80EEA', e.target.value)}
          disabled={isReadOnly}
          error={errors.section80EEA}
        />
            {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
              <SectionUpload 
                sectionName="Interest on Home Loan (80EEA)" 
                onUpload={handleProofUpload}
                onApprove={handleApproveAttachment}
                onReject={handleRejectAttachment}
                onRemove={handleRemoveAttachment}
                disabled={isReadOnly}
                initialAttachmentId={getFieldAttachment('Interest on Home Loan (80EEA)')?.attachmentId}
                initialFileName={getFieldAttachment('Interest on Home Loan (80EEA)')?.fileName}
                initialFileSize={getFieldAttachment('Interest on Home Loan (80EEA)')?.size}
                initialStatus={getFieldAttachment('Interest on Home Loan (80EEA)')?.status || 'pending'}
                initialRejectReason={getFieldAttachment('Interest on Home Loan (80EEA)')?.comment || ''}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
        <FloatingInput
          id="section80EEB"
          label="Interest on Home Loan (80EEB)"
          type="number"
          value={declarationData.sectionVIA.otherInvestments.section80EEB}
          onChange={(e) => handleInputChange('sectionVIA', 'otherInvestments', 'section80EEB', e.target.value)}
          disabled={isReadOnly}
          error={errors.section80EEB}
        />
            {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
              <SectionUpload 
                sectionName="Interest on Home Loan (80EEB)" 
                onUpload={handleProofUpload}
                onApprove={handleApproveAttachment}
                onReject={handleRejectAttachment}
                onRemove={handleRemoveAttachment}
                disabled={isReadOnly}
                initialAttachmentId={getFieldAttachment('Interest on Home Loan (80EEB)')?.attachmentId}
                initialFileName={getFieldAttachment('Interest on Home Loan (80EEB)')?.fileName}
                initialFileSize={getFieldAttachment('Interest on Home Loan (80EEB)')?.size}
                initialStatus={getFieldAttachment('Interest on Home Loan (80EEB)')?.status || 'pending'}
                initialRejectReason={getFieldAttachment('Interest on Home Loan (80EEB)')?.comment || ''}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
        <FloatingInput
          id="section80G"
          label="Donations (80G)"
          type="number"
          value={declarationData.sectionVIA.otherInvestments.section80G}
          onChange={(e) => handleInputChange('sectionVIA', 'otherInvestments', 'section80G', e.target.value)}
          disabled={isReadOnly}
          error={errors.section80G}
        />
            {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
              <SectionUpload 
                sectionName="Donations (80G)" 
                onUpload={handleProofUpload}
                onApprove={handleApproveAttachment}
                onReject={handleRejectAttachment}
                onRemove={handleRemoveAttachment}
                disabled={isReadOnly}
                initialAttachmentId={getFieldAttachment('Donations (80G)')?.attachmentId}
                initialFileName={getFieldAttachment('Donations (80G)')?.fileName}
                initialFileSize={getFieldAttachment('Donations (80G)')?.size}
                initialStatus={getFieldAttachment('Donations (80G)')?.status || 'pending'}
                initialRejectReason={getFieldAttachment('Donations (80G)')?.comment || ''}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
        <FloatingInput
          id="section80TTA"
          label="Interest on Savings Account (80TTA)"
          type="number"
          value={declarationData.sectionVIA.otherInvestments.section80TTA}
          onChange={(e) => handleInputChange('sectionVIA', 'otherInvestments', 'section80TTA', e.target.value)}
          disabled={isReadOnly}
          error={errors.section80TTA}
        />
            {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
              <SectionUpload 
                sectionName="Interest on Savings Account (80TTA)" 
                onUpload={handleProofUpload}
                onApprove={handleApproveAttachment}
                onReject={handleRejectAttachment}
                onRemove={handleRemoveAttachment}
                disabled={isReadOnly}
                initialAttachmentId={getFieldAttachment('Interest on Savings Account (80TTA)')?.attachmentId}
                initialFileName={getFieldAttachment('Interest on Savings Account (80TTA)')?.fileName}
                initialFileSize={getFieldAttachment('Interest on Savings Account (80TTA)')?.size}
                initialStatus={getFieldAttachment('Interest on Savings Account (80TTA)')?.status || 'pending'}
                initialRejectReason={getFieldAttachment('Interest on Savings Account (80TTA)')?.comment || ''}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
        <FloatingInput
          id="section80TTB"
          label="Interest on Deposits (80TTB)"
          type="number"
          value={declarationData.sectionVIA.otherInvestments.section80TTB}
          onChange={(e) => handleInputChange('sectionVIA', 'otherInvestments', 'section80TTB', e.target.value)}
          disabled={isReadOnly}
          error={errors.section80TTB}
        />
            {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
              <SectionUpload 
                sectionName="Interest on Deposits (80TTB)" 
                onUpload={handleProofUpload}
                onApprove={handleApproveAttachment}
                onReject={handleRejectAttachment}
                onRemove={handleRemoveAttachment}
                disabled={isReadOnly}
                initialAttachmentId={getFieldAttachment('Interest on Deposits (80TTB)')?.attachmentId}
                initialFileName={getFieldAttachment('Interest on Deposits (80TTB)')?.fileName}
                initialFileSize={getFieldAttachment('Interest on Deposits (80TTB)')?.size}
                initialStatus={getFieldAttachment('Interest on Deposits (80TTB)')?.status || 'pending'}
                initialRejectReason={getFieldAttachment('Interest on Deposits (80TTB)')?.comment || ''}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
        </div>
        
        {/* Bulk Actions for Other Investments */}
        {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
          <div className="mt-4">
            <BulkActions 
              sectionName="Other Investments" 
              onBulkAction={handleBulkAction} 
          disabled={isReadOnly}
        />
          </div>
        )}
      </div>
    </div>
  );


  
  const renderPreviousEmployment = () => {
    const isReadOnly = isInvestmentDeclarationReadOnly();
    const isRequired = requiresPrevEmploymentDetails();

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold mb-4">Previous Employment</h3>
        {!isRequired && (
          <p className="text-sm text-gray-600">
            Optional for employees without previous work experience. Leave blank to
            save as Not Applicable with zero salary and tax.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FloatingInput
            id="prevEmployerName"
            label="Previous Employer Name"
            value={prevEmployment.employerName}
            error={errors.prevEmployerName}
            onChange={(e) => {
              const value = e.target.value;
              // Only allow letters and spaces
              if (value === '' || /^[A-Za-z\s]+$/.test(value)) {
              setPrevEmployment((prev) => ({
                ...prev,
                  employerName: value,
                }));
                // Clear error when user types (validation will happen on Save & Next)
                if (errors.prevEmployerName) {
                  setFieldError('prevEmployerName', '');
                }
            }
            }}
            disabled={isReadOnly}
            required={isRequired}
          />

          <FloatingInput
            id="prevGrossSalary"
            label="Total Salary Received (Gross)"
            type="number"
            value={prevEmployment.grossSalary}
            error={errors.prevGrossSalary}
            onChange={(e) =>
              setPrevEmployment((prev) => {
                const nextValue = e.target.value;
                // Clear error when user types (validation will happen on Save & Next)
                if (errors.prevGrossSalary) {
                  setFieldError('prevGrossSalary', '');
                }
                // Clear related tax error if it was about comparison
                if (errors.prevTaxDeducted && errors.prevTaxDeducted.includes('must be less than')) {
                  setFieldError('prevTaxDeducted', '');
                }
                return {
                  ...prev,
                  grossSalary: nextValue,
                };
              })
            }
            disabled={isReadOnly}
            required={isRequired}
          />

          <FloatingInput
            id="prevTaxDeducted"
            label="Total Tax Deducted"
            type="number"
            value={prevEmployment.taxDeducted}
            error={errors.prevTaxDeducted}
            onChange={(e) =>
              setPrevEmployment((prev) => {
                const nextValue = e.target.value;
                // Clear error when user types (validation will happen on Save & Next)
                if (errors.prevTaxDeducted) {
                  setFieldError('prevTaxDeducted', '');
                }
                return {
                  ...prev,
                  taxDeducted: nextValue,
                };
              })
            }
            disabled={isReadOnly}
            required={isRequired}
          />
        </div>
      </div>
    );
  };

  // 4. House Property (multiple entries)
  const renderHousePropertyDetails = () => {
    const housePropertyEntries = declarationData.housePropertyEntries || [];

    return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">House Property Details</h3>
      
      {housePropertyEntries.map((entry, index) => (
        <div key={index} className="p-4 bg-gray-50 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`propertyType-${index}`}
                  value="self-occupied"
                  checked={entry.propertyType === 'self-occupied'}
                  onChange={(e) => handleHousePropertyChange(index, 'propertyType', e.target.value)}
                  className="mr-2"
                  disabled={isReadOnly}
                />
                Self Occupied
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`propertyType-${index}`}
                  value="let-out"
                  checked={entry.propertyType === 'let-out'}
                  onChange={(e) => handleHousePropertyChange(index, 'propertyType', e.target.value)}
                  className="mr-2"
                  disabled={isReadOnly}
                />
                Let Out
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id={`loanTakenBeforeApril1999-${index}`}
                checked={entry.loanTakenBeforeApril1999}
                onChange={(e) =>
                  handleHousePropertyChange(
                    index,
                    'loanTakenBeforeApril1999',
                    e.target.checked,
                  )
                }
                className="mr-2"
                disabled={isReadOnly}
              />
              <label htmlFor={`loanTakenBeforeApril1999-${index}`}>
                Loan taken before April 1999
              </label>
            </div>
            <FloatingInput
              id={`interest-${index}`}
              label="Interest paid on borrowed capital"
              type="number"
              value={entry.interest}
              onChange={(e) => handleHousePropertyChange(index, 'interest', e.target.value)}
              disabled={isReadOnly}
              error={errors[`interest-${index}`]}
              required
            />
            {entry.propertyType === 'let-out' && (
              <FloatingInput
                id={`rentalIncome-${index}`}
                label="Annual rental income"
                type="number"
                value={entry.rentalIncome}
                onChange={(e) =>
                  handleHousePropertyChange(index, 'rentalIncome', e.target.value)
                }
                disabled={isReadOnly}
                error={errors[`rentalIncome-${index}`]}
                required
              />
            )}
            <FloatingInput
              id={`propertyAddress-${index}`}
              label="Property Address"
              value={entry.address}
              onChange={(e) => handleHousePropertyChange(index, 'address', e.target.value)}
              disabled={isReadOnly}
              error={errors[`propertyAddress-${index}`]}
              required
            />
          </div>

          {/* Section-level upload for each House Property entry */}
          {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
            <div className="mt-4">
              <SectionUpload
                sectionName={`House Property - Entry ${index + 1}`}
                onUpload={handleProofUpload}
                onApprove={handleApproveAttachment}
                onReject={handleRejectAttachment}
                onRemove={handleRemoveAttachment}
                disabled={isReadOnly}
                isSectionLevel={true}
                initialAttachmentId={getFieldAttachment(`House Property - Entry ${index + 1}`)?.attachmentId}
                initialFileName={getFieldAttachment(`House Property - Entry ${index + 1}`)?.fileName}
                initialFileSize={getFieldAttachment(`House Property - Entry ${index + 1}`)?.size}
                initialStatus={getFieldAttachment(`House Property - Entry ${index + 1}`)?.status || 'pending'}
                initialRejectReason={getFieldAttachment(`House Property - Entry ${index + 1}`)?.comment || ''}
                onViewAttachment={openAttachment}
              />
            </div>
          )}

          {!isReadOnly && index > 0 && (
            <button
              type="button"
              className="mt-2 text-red-600 hover:text-red-800 text-sm"
              onClick={() => removeHousePropertyEntry(index)}
            >
              Remove House Property
            </button>
          )}
        </div>
      ))}

      {/* {!isReadOnly && (
        <button
          type="button"
          onClick={addHousePropertyEntry}
          className="m-2 text-redhalo-navy hover:text-redhalo-navy-dark text-sm"
        >
          + Add House Property
        </button>
      )} */}
    </div>
  );
  };

  // --------------------- TABS DEFINITION ---------------------
  const showPrevEmploymentTab = true;

  const tabs = [
    {
      id: 'prevEmployment',
      label: 'Previous Employment',
      content: renderPreviousEmployment,
    },
    { id: 'hra', label: 'HRA', content: renderHRA },
    { id: 'section80C', label: 'Section 80C', content: renderSection80C },
    { id: 'viaDeductions', label: 'VIA-deductions', content: renderVIADeductions },
    { id: 'houseProperty', label: 'House Property', content: renderHousePropertyDetails },
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative bg-white shadow-xl w-[1200px] h-[800px] flex flex-col">
        {/* Modal Header - Fixed */}
        <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-redhalo-mint/50 to-gray-panel flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Investment Declaration</h2>
            <p className="text-sm text-gray-600 mt-1">
              {employee?.user?.name} {employee?.user?.lastName || ''} - {employee?.empCode}
            </p>
          </div>
          <button 
            onClick={handleCancelOrClose} 
            className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        {/* Admin Tax Regime Selection + Employee Access Overrides - Fixed */}
        <div className="p-4 border-b bg-gray-50 flex-shrink-0 space-y-3">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Tax Regime:</label>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="taxRegime"
                  value="old"
                  checked={selectedTaxRegime === 'old'}
                  onChange={async (e) => {
                    const newRegime = e.target.value;
                    setSelectedTaxRegime(newRegime);
                    if (employee) {
                      employee.taxRegime = newRegime;
                    }
                    
                    // Save tax regime change immediately
                    try {
                      const payload = {
                        financialYear: getEffectiveFinancialYear(),
                        taxRegime: newRegime === 'old' ? 'OLD' : 'NEW',
                      };
                      const result = await payrollService.saveInvestmentData(employee.id, payload);
                      if (result?.success) {
                        // Notify parent component to refresh data (for list/grid view updates)
                        if (typeof onSave === 'function') {
                          onSave({ 
                            type: 'tax_regime_changed',
                            taxRegime: newRegime,
                            declarationData 
                          });
                        }
                      }
                    } catch (error) {
                      console.error('Error saving tax regime change:', error);
                      toast.error('Failed to save tax regime change');
                    }
                  }}
                  className="mr-2"
                  disabled={isReadOnly || !taxRegimeChangeAllowed}
                />
                <span className="text-sm">Old Regime</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="taxRegime"
                  value="new"
                  checked={selectedTaxRegime === 'new'}
                  onChange={async (e) => {
                    const newRegime = e.target.value;
                    setSelectedTaxRegime(newRegime);
                    if (employee) {
                      employee.taxRegime = newRegime;
                    }
                    
                    // Save tax regime change immediately
                    try {
                      const payload = {
                        financialYear: getEffectiveFinancialYear(),
                        taxRegime: newRegime === 'old' ? 'OLD' : 'NEW',
                      };
                      const result = await payrollService.saveInvestmentData(employee.id, payload);
                      if (result?.success) {
                        // Notify parent component to refresh data (for list/grid view updates)
                        if (typeof onSave === 'function') {
                          onSave({ 
                            type: 'tax_regime_changed',
                            taxRegime: newRegime,
                            declarationData 
                          });
                        }
                      }
                    } catch (error) {
                      console.error('Error saving tax regime change:', error);
                      toast.error('Failed to save tax regime change');
                    }
                  }}
                  className="mr-2"
                  disabled={isReadOnly || !taxRegimeChangeAllowed}
                />
                <span className="text-sm">New Regime</span>
              </label>
            </div>
          </div>

          {mode === 'edit' && selectedTaxRegime === 'old' && (
            <div className="mt-2 border-t border-gray-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-gray-600">Employee Access Overrides</div>
                <button
                  onClick={handleSaveEmployeeAccessOverrides}
                  disabled={isReadOnly}
                  className={`px-3 py-1.5 text-xs font-medium rounded-none transition-colors ${
                    isReadOnly
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : darkMode
                      ? 'rh-btn-primary hover:opacity-95'
                      : 'rh-btn-primary hover:opacity-95'
                  }`}
                >
                  Save
                </button>
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-gray-700">
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={allowAllEdits}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAllowAllEdits(checked);
                      // If unchecking "Allow all sections", uncheck all others
                      if (!checked) {
                        setAllowPrevEmploymentEdit(false);
                        setAllowHraEdit(false);
                        setAllowSection80CEdit(false);
                        setAllowViaEdit(false);
                        setAllowHousePropertyEdit(false);
                      }
                    }}
                    disabled={isReadOnly}
                  />
                  <span>Allow all sections</span>
                </label>
                {showPrevEmploymentTab && (
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      className="mr-1"
                      checked={allowPrevEmploymentEdit}
                      onChange={(e) => setAllowPrevEmploymentEdit(e.target.checked)}
                      disabled={isReadOnly}
                    />
                    <span>Prev Employment</span>
                  </label>
                )}
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={allowHraEdit}
                    onChange={(e) => setAllowHraEdit(e.target.checked)}
                    disabled={isReadOnly}
                  />
                  <span>HRA</span>
                </label>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={allowSection80CEdit}
                    onChange={(e) => setAllowSection80CEdit(e.target.checked)}
                    disabled={isReadOnly}
                  />
                  <span>Section 80C</span>
                </label>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={allowViaEdit}
                    onChange={(e) => setAllowViaEdit(e.target.checked)}
                    disabled={isReadOnly}
                  />
                  <span>VIA-deductions</span>
                </label>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={allowHousePropertyEdit}
                    onChange={(e) => setAllowHousePropertyEdit(e.target.checked)}
                    disabled={isReadOnly}
                  />
                  <span>House Property</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Tab Navigation - Fixed */}
        {selectedTaxRegime === 'old' && (
          <div className="flex border-b border-gray-200">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                className={`px-6 py-3 font-medium text-sm transition-colors ${
                  currentSectionIndex === index
                    ? `${
                        darkMode
                          ? "bg-[#BB86FC] text-white border-b-2 border-[#BB86FC]"
                          : "rh-btn-primary border-b-2 border-redhalo-navy"
                      }`
                    : `${
                        darkMode
                          ? "text-[#A6A9C8] hover:bg-[#3C3C3C]"
                          : "text-gray-600 hover:bg-gray-100"
                      }`
                }`}
                onClick={() => goToSection(tab.id)}
              >
                <div className="flex items-center space-x-2">
                  <span>{tab.label}</span>
                  {savedSections.has(tab.id) && (
                    <span className="text-green-500">✓</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Section Progress Indicator */}
        {selectedTaxRegime === 'old' && (
          <div className="p-3 bg-redhalo-mint/40 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">
                    Section {currentSectionIndex + 1} of {tabs.length}:
                  </span>
                  <span className="text-sm text-redhalo-navy font-medium">
                    {getCurrentSection()?.label}
                  </span>
                </div>

                {/* Live totals for specific sections */}
               {getCurrentSection()?.id === 'section80C' && (
                <div className="px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-medium flex gap-6">
                  
                  <div>
                    Total 80C:
                    <span className="ml-1">
                      {getSection80CTotal().toLocaleString('en-IN', {
                        maximumFractionDigits: 0,
                      })}
                    </span>
                  </div>

                  <div>
                    Total eligible 80C Exemption:
                    <span className="ml-1">
                      {Math.min(getSection80CTotal(), 150000).toLocaleString('en-IN', {
                        maximumFractionDigits: 0,
                      })}
                    </span>
                  </div>

                </div>
              )}

                {getCurrentSection()?.id === 'viaDeductions' && (
                  <div className="px-2 py-1 rounded rh-accent-soft text-xs font-medium">
                    Total VI-A: {getViaDeductionsTotal().toLocaleString('en-IN', {
                      maximumFractionDigits: 0,
                    })}
                  </div>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={goToPreviousSection}
                  disabled={currentSectionIndex === 0}
                  className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Previous
                </button>
                <button
                  onClick={goToNextSection}
                  disabled={currentSectionIndex === tabs.length - 1}
                  className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content - Scrollable */}
        <div className="flex-grow overflow-y-auto p-4 bg-gray-50">
          <div className="bg-white shadow-sm p-6 h-full">
            {selectedTaxRegime === 'old' ? (
              getCurrentSection()?.content()
            ) : (
              <div className="space-y-6">
                {renderPreviousEmployment()}
                <div className="text-center py-4">
                  <div className="bg-green-50 border border-green-200 p-6">
                    <h3 className="text-lg font-semibold text-green-800 mb-2">New Tax Regime Selected</h3>
                    <p className="text-green-600">
                      No additional deductions applicable under the new tax regime.
                      <br />
                      If you have any tax saving investments, select old tax regime and save your investments.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Action Buttons - Fixed */}
        <div className="border-t p-4 bg-white flex justify-between items-center flex-shrink-0">
          <div className="flex space-x-3">
            {mode === 'edit' && selectedTaxRegime === 'old' && (
              <>
                {/* Show different button text based on current section */}
                <button
                  onClick={saveCurrentSection}
                  disabled={isReadOnly}
                  className={`px-4 py-2 rounded flex items-center space-x-2 ${isReadOnly ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-500 text-white hover:bg-green-600'}`}
                >
                  {/* <span>💾</span> */}
                  <span>{getCurrentSection()?.id === 'houseProperty' ? 'Save' : 'Save and Next'}</span>
                </button>
                <button
                  onClick={resetCurrentSection}
                  disabled={isReadOnly}
                  className={`px-4 py-2 rounded flex items-center space-x-2 ${isReadOnly ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-orange-500 text-white hover:bg-orange-600'}`}
                >
                  {/* <span>🔄</span> */}
                  <span>Reset Section</span>
                </button>
              </>
            )}
          </div>
          
          <div className="flex space-x-3">
            {/* Show Submit Declaration button only on House Property section for Old Regime */}
            {mode === 'edit' && selectedTaxRegime === 'old' && getCurrentSection()?.id === 'houseProperty' && (
              <button
                onClick={submitDeclaration}
                disabled={isReadOnly}
                className={`px-6 py-2 rounded flex items-center space-x-2 ${isReadOnly ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'rh-btn-primary hover:opacity-95'}`}
              >
                <span>Submit Declaration</span>
              </button>
            )}

            {/* Save button for New Regime, shown next to Cancel (uses same confirmation flow) */}
            {mode === 'edit' && selectedTaxRegime === 'new' && (
              <button
                onClick={submitDeclaration}
                disabled={isReadOnly}
                className={`px-6 py-2 rounded flex items-center space-x-2 ${isReadOnly ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'rh-btn-primary hover:opacity-95'}`}
              >
                <span>Save</span>
              </button>
            )}

            <button
              onClick={handleCancelOrClose}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
            >
              {mode === 'view' ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>

        {/* Success Notification */}
        {showSuccessNotification && (
          <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50">
            Declaration submitted successfully!
          </div>
        )}
      </div>

      {/* ProofDocumentModal (for uploading documents) */}
      <ProofDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleDocumentUpload}
      />
      
      {/* Submit Success Modal */}
      <SubmitSuccessModal
        isOpen={showSubmitSuccessModal}
        onClose={handleSubmitSuccessClose}
      />

      {/* Cancel warning when Old Regime (nothing declared) or New Regime selected */}
      {cancelConfirmMode && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {cancelConfirmMode === 'old_nothing_declared' ? 'Cancel without declaring?' : 'Cancel - New Regime selected'}
              </h3>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm text-gray-700">
              {cancelConfirmMode === 'old_nothing_declared' ? (
                <>
                  <p>
                    You have chosen <span className="font-semibold">Old Regime</span> but not declared anything.
                    It may lead to tax deduction according to the Old tax regime.
                  </p>
                  <p>Do you still want to cancel?</p>
                </>
              ) : (
                <>
                  <p>
                    You have chosen <span className="font-semibold">New Regime</span>.
                  </p>
                  <p>
                    All data (including attachments) saved under the Old Regime will be <span className="font-semibold">deleted</span> when you proceed.
                  </p>
                  <p>Do you still want to cancel?</p>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-3 bg-gray-50">
              <button
                type="button"
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 text-sm hover:bg-gray-100"
                onClick={() => setCancelConfirmMode(null)}
              >
                No
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded rh-btn-primary text-sm hover:opacity-95"
                onClick={() => {
                  setCancelConfirmMode(null);
                  onClose();
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Declaration Confirmation Modal */}
      {submitConfirm.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {submitConfirm.mode === 'old'
                  ? 'Submit Declaration - Old Tax Regime'
                  : 'Submit Declaration - New Tax Regime'}
              </h3>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm text-gray-700">
              {submitConfirm.mode === 'old' ? (
                <>
                  <p>
                    Are you sure you want to submit the investment declaration under the
                    <span className="font-semibold"> Old Tax Regime</span> for this employee?
                  </p>
                  <p>
                    After submission, you may not be able to edit these details if the
                    investment declaration window is locked by your employer.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    You have selected the <span className="font-semibold">New Tax Regime</span>.
                  </p>
                  <p>
                    All data (including attachments) saved under the Old Regime for this
                    employee will be <span className="font-semibold">deleted</span>, except
                    <span className="font-semibold"> Previous Employment</span> details.
                  </p>
                  <p className="text-red-600">
                    This action cannot be undone. Do you wish to proceed and save under the
                    New Regime?
                  </p>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3 bg-gray-50">
              <button
                type="button"
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 text-sm hover:bg-gray-100"
                onClick={() => setSubmitConfirm({ open: false, mode: null })}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded rh-btn-primary text-sm hover:opacity-95"
                onClick={handleConfirmSubmit}
              >
                Confirm Submit
              </button>
            </div>
          </div>
        </div>
      )}

      <AttachmentPreviewModal
        isOpen={attachmentPreview.isOpen}
        url={attachmentPreview.url}
        downloadUrl={attachmentPreview.downloadUrl}
        fileName={attachmentPreview.fileName}
        onClose={() =>
          setAttachmentPreview({
            isOpen: false,
            url: null,
            downloadUrl: null,
            fileName: '',
          })
        }
      />
    </div>
  );
}

// --------------------- Submit Success Modal ---------------------
function SubmitSuccessModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[100] flex items-center justify-center p-4">
      <div className="relative bg-white shadow-xl w-full max-w-md">
        <div className="p-6 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Declaration Submitted Successfully!
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            Your investment declaration has been submitted and TDS will be computed accordingly.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 rh-btn-primary rounded-md hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-2"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

// --------------------- ProofDocumentModal ---------------------
function ProofDocumentModal({ isOpen, onClose, onUpload }) {
  const [selectedDocument, setSelectedDocument] = useState('');
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size <= 10 * 1024 * 1024) {
      // 10MB limit
      setFile(selectedFile);
    } else {
      alert('File size should not exceed 10MB');
    }
  };

  const handleUpload = () => {
    if (selectedDocument && file) {
      onUpload({
        documentName: selectedDocument,
        fileName: file.name,
        size: file.size,
        lastUpdated: new Date().toISOString(),
        comment,
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center overflow-auto ${
        isOpen ? 'block' : 'hidden'
      }`}
    >
      <div className="bg-white p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Upload Document</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="relative">
            <select
              value={selectedDocument}
              onChange={(e) => setSelectedDocument(e.target.value)}
              className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-white rounded-none border-0 border-b border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-b-2 focus:border-redhalo-red hover:border-b-redhalo-navy peer"
            >
              <option value="">Select document type</option>
              <option value="House Rent (HRA)">House Rent (HRA)</option>
              <option value="Section 80C">Section 80C</option>
              <option value="Section 80D">Section 80D</option>
              <option value="Section 80DD">Section 80DD</option>
              <option value="Section 80DDB">Section 80DDB</option>
              <option value="Section 80E">Section 80E</option>
              <option value="Section 80EE">Section 80EE</option>
              <option value="Section 80U">Section 80U</option>
              <option value="Section 80CCD(1B)">Section 80CCD(1B)</option>
              <option value="House Property Documents">House Property Documents</option>
            </select>
            <label
              className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 rounded-none 
                peer-focus:px-2 peer-focus:text-redhalo-navy 
                peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 
                peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 
                peer-focus:-translate-y-4 left-1"
            >
              Select Document
            </label>
          </div>

          <div className="relative">
            <input
              type="file"
              onChange={handleFileChange}
              className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-none border-0 border-b border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-b-2 focus:border-redhalo-red hover:border-b-redhalo-navy peer"
            />
            <label
              className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 rounded-none 
                peer-focus:px-2 peer-focus:text-redhalo-navy 
                peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 
                peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 
                peer-focus:-translate-y-4 left-1"
            >
              File
            </label>
            <p className="text-xs text-gray-500 mt-1">Max limit for each file is 10 MB</p>
          </div>

          <div className="relative">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows="2"
              placeholder=" "
              className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-none border-0 border-b border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-b-2 focus:border-redhalo-red hover:border-b-redhalo-navy peer"
            ></textarea>
            <label
              className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 rounded-none 
                peer-focus:px-2 peer-focus:text-redhalo-navy 
                peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 
                peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 
                peer-focus:-translate-y-4 left-1"
            >
              Comment
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 border rounded text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleUpload}
            className="px-4 py-2 bg-redhalo-mint/400 text-white rounded text-sm hover:opacity-95"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
}
